/**
 * Bioclast scene — Three.js port of the legacy `app/templates/particles.html`
 * million-point galaxy, reduced to a portable WebGL2 build using Three.js.
 *
 * Visual layers (toggle via `visualMode`):
 *   0 = Particles only
 *   1 = Particles + Strings
 *   2 = Particles + Lattice
 *   3 = Hybrid (particles + strings + lattice + aurora + warp)
 *
 * Audio reactivity:
 *   - bass         : core gravity + cymatic shockwaves
 *   - mid          : flow swirl
 *   - treble       : sparkle / particle size shimmer
 *   - beat envelope: pulse + cymatic ring birth
 *   - level        : global brightness / size
 *
 * Particle physics runs on the CPU (typed arrays); positions stream into a
 * three.js BufferAttribute every frame. ~60k particles is a comfortable
 * default on modern laptops; tunable via `particleCount`.
 */

import * as THREE from "three";

import { AudioFeatures, ZERO_FEATURES } from "../../audio/types";

export interface BioclastParams {
  paletteHue: number; // 0..360
  intensity: number; // 0..2
  visualMode: 0 | 1 | 2 | 3;
  colorMode: 0 | 1 | 2 | 3; // 0 ice, 1 rivers, 2 velocity, 3 density
  starSize: number; // 0.05..1
  saturation: number; // 0..1 — particle color saturation
  opacity: number; // 0..1 — global transparency multiplier
  atmosphere: number; // 0..1.5 — backdrop opacity multiplier
  backdropBlur: number; // 0..120 px — backdrop blur radius
  bassGravity: number; // 0..4
  midCurl: number; // 0..4
  trebleShimmer: number; // 0..4
  cymaticStrength: number; // 0..4
  flowSpeed: number; // 0..4
  cameraSpin: number; // 0..3 — auto-rotate rate
  cameraZoom: number; // 0.15..3.5 — 1.0 is the default framing; <0.5 pulls way back for lava-lamp view
  cameraTilt: number; // 0..1 — 0 = horizon level, 1 = top-down
  cameraOrbit: number; // radians — manual yaw offset (drag to set)
  /** Orbit (third-person around the field) or flythrough (first-person path through it). */
  cameraMode: CameraMode;
  /** Speed multiplier for flythrough mode. */
  flythroughSpeed: number; // 0.1..3
  /** Forward dolly punch on each detected beat (flythrough mode). 0..1 */
  beatDolly: number;
  /** Auto-rotate the framing preset every `autopilotInterval` seconds. */
  autopilotEnabled: boolean;
  /** Seconds between autopilot framing changes. */
  autopilotInterval: number; // 2..30
  /** Snap to a new framing only on the next strong beat. */
  autopilotBeatLock: boolean;
  fieldTightness: number; // 0.4..1.6 — squeeze (low) or spread (high) the particle field

  // -------------------------------------------------------------------------
  // Layer toggles — each one independently controls a visual element.
  // Scenes set these explicitly; users can flip individual layers to add or
  // remove elements without touching the scene's other tuning.
  // -------------------------------------------------------------------------
  /** Particle cloud (the core of every scene). */
  layerParticles: boolean;
  /** Curling string strands swirling through the field. */
  layerStrings: boolean;
  /** Concentric ring lattice. */
  layerLattice: boolean;
  /** Aurora ribbons drifting overhead. */
  layerAurora: boolean;
  /** Camera-locked warp tunnel (stars rushing past). */
  layerWarp: boolean;
  /** Vertical pillars (cathedral architecture). */
  layerCathedral: boolean;
  /** Camera-locked DNA helix flying past. */
  layerDnaHelix: boolean;
  /** Inward-spiraling pulse vortex with beat-driven shockwave rings. */
  layerVortex: boolean;
  /** Live-FFT terrain wave (frequency spectrum as 3D landscape). */
  layerTide: boolean;
  /** Concentric particle rings, each tuned to a different frequency band. */
  layerOrbital: boolean;
  /** Galaxy disc with spiral arms + warp-streak foreground stars. */
  layerGalaxy: boolean;
  /** Vertical aurora ribbons forming a forest you fly through. */
  layerAuroraForest: boolean;
  /** Boid swarm with separation/alignment/cohesion + beat-driven fear. */
  layerBoids: boolean;
  /** Particle wind tunnel — organic forward-flowing tunnel with beat flips. */
  layerWind: boolean;
  /** Reflective mercury pool surface in front of camera, beat ripples. */
  layerMercury: boolean;
  /** Mountain Range terrain you fly forward through. */
  layerMountains: boolean;

  // -------------------------------------------------------------------------
  // Mesh-engine sliders — shared by Audio Tide, Mercury Pool, Mountain Range.
  // Surface-only for now (UI persists values; renderer wiring comes later).
  // -------------------------------------------------------------------------
  /** Mesh grid width in cells. 20–200. */
  meshGridWidth: number;
  /** Mesh grid depth in cells. 20–200. */
  meshGridDepth: number;
  /** World size of each mesh cell. 0.05–0.5. */
  meshCellSize: number;
  /** Vertical wave amplitude. 0–3. */
  meshWaveHeight: number;
  /** Depth-axis wave amplitude. 0–2. */
  meshWaveDepth: number;
  /** Procedural noise complexity (octaves blend). 0–1. */
  meshComplexity: number;
  /** Harmonic overtone strength. 0–1. */
  meshHarmonics: number;
  /** How sharply the mesh responds to a beat hit. 0–1. */
  meshBeatSharpness: number;

  /** Raymarched volumetric energy sphere with an additive atmosphere shell. */
  layerEnergyOrb: boolean;
  /**
   * Waveform overlay — a glowing line of the live audio waveform drawn
   * camera-locked across the screen. Inspired by MilkDrop's signature
   * DrawWave pass; pairs with any scene to add the "you can SEE the
   * music" layer that classic Winamp visualizers had.
   */
  layerWaveform: boolean;
  /** Waveform style: line, dual-stereo, or radial circle. */
  waveformStyle: "line" | "dual" | "radial";
  /**
   * Particle physics mode. The default "orbital" runs the original
   * gravitational/swirl physics that powers all 19 hand-tuned scenes.
   * "emergence" swaps in a self-organizing attractor + noise + cohesion
   * field that morphs the cloud into target shapes — reads as a lava
   * lamp / blob simulation that responds to music by deforming the
   * underlying shape rather than orbiting.
   */
  particleMode: "orbital" | "emergence";
  /** Source-grounded field identity for the shared GPU Flow-Form renderer. */
  flowFormLook: FlowFormLookId;
  /**
   * Target shape the emergence physics blends toward each frame. Bass
   * causes the cloud to release toward chaos; quiet sections let it
   * snap back into the chosen shape.
   */
  emergenceShape: "sphere" | "torus" | "cube" | "ribbon" | "blob";
  /**
   * Particle quality tier — drives the active particle count across
   * every scene. Lower = faster on weak hardware; higher = denser/richer
   * visuals on desktop / Ultra GPUs.
   */
  qualityTier: QualityTier;

  /** Live math shader particle count. Clamped per quality tier at render time. */
  mathParticleCount: number;
  /** Live math shader point size multiplier. */
  mathPointSize: number;
  /** Live math shader geometry deformation amplitude. */
  mathAmplitude: number;
  /** Live math shader topology frequency/detail. */
  mathFrequency: number;
  /** Live math shader orbit and phase speed. */
  mathRotationSpeed: number;
  /** Live math shader depth/projection scale. */
  mathDepthScale: number;
  /** Palette mix from cooler teal accents to warm gold. */
  mathWarmth: number;
  /** Hue distance between palette stops. 0 is monochrome; 1 spans the spectrum. */
  mathColorSpread: number;
  /** Speed and audio influence of palette bands traveling through the form. */
  mathColorFlow: number;

  /** Enable bloom (neon-glow) post-processing. */
  bloomEnabled: boolean;
  /** Bloom intensity multiplier. 0..3 */
  bloomStrength: number;
  /** Bloom blur radius. 0..1.2 */
  bloomRadius: number;
  /** Luminance threshold above which pixels bloom. 0..1 */
  bloomThreshold: number;

  // -------------------------------------------------------------------------
  // Feedback / trail effect (MilkDrop-style frame history)
  // 0 strength = no trails (default). Higher = particles leave trails that
  // drift, swirl, and dissolve based on the warp params below.
  // -------------------------------------------------------------------------
  /** Feedback strength 0..0.95. 0 = off (zero perf cost). */
  feedbackStrength: number;
  /** Per-frame zoom of the previous frame: -0.05..0.05. Positive = zoom in. */
  feedbackZoom: number;
  /** Per-frame rotation in radians: -0.1..0.1. */
  feedbackRotate: number;
  /** Per-frame horizontal drift: -0.02..0.02. */
  feedbackDriftX: number;
  /** Per-frame vertical drift: -0.02..0.02. */
  feedbackDriftY: number;
  /** Decay factor on each previous-frame sample: 0..1. Closer to 1 = slower fade. */
  feedbackDecay: number;

  /** Concept preset (overrides several look knobs each frame). */
  conceptMode: ConceptMode;

  // -------------------------------------------------------------------------
  // Programmatic camera motion (vizz.fm-style sin-wave automation).
  // Each pair is amount (0..1+) + period (seconds). Renderer applies these
  // *after* the scene places the camera, so they layer on top of orbit /
  // flythrough modes without fighting them.
  // -------------------------------------------------------------------------
  /** Continuous yaw rate, radians/sec. */
  camAutoRotateH: number;
  /** Continuous pitch rate, radians/sec. */
  camAutoRotateV: number;
  /** Continuous roll rate, radians/sec. */
  camAutoRoll: number;
  /** Sine-wave zoom amount, 0..1 (multiplier on cameraZoom). */
  camZoomAmount: number;
  /** Zoom oscillation period, seconds. */
  camZoomPeriod: number;
  /** Sine-wave FOV breathing amount in degrees. */
  camFovAmount: number;
  /** FOV oscillation period, seconds. */
  camFovPeriod: number;
  /** Horizontal pan amount in world units. */
  camPanAmount: number;
  /** Pan period, seconds. */
  camPanPeriod: number;

  // -------------------------------------------------------------------------
  // Post-processing FX stack — vizz.fm-style chained effects.
  // Each is 0..1 strength; 0 = off. See postFxPass.ts for ordering.
  // -------------------------------------------------------------------------
  postFxEnabled: boolean;
  fxRgbShift: number;
  fxGlitch: number;
  fxCrt: number;
  fxKaleidoscope: number;
  fxPixelate: number;
  fxDither: number;
  fxDotScreen: number;
  fxFilmGrain: number;
  fxTiltShift: number;
  fxRadialBlur: number;
  fxVignette: number;
  fxSwirl: number;
  fxBarrel: number;
  fxKaleidoscopeSegments: number;
  fxGridTile: number;
  fxGridTileColumns: number;

  // Extended post-fx params (vizz.fm parity).
  fxRgbShiftAngle: number;
  fxRgbShiftOpacity: number;
  fxGlitchSpeed: number;
  fxGlitchBlock: number;
  fxGlitchOpacity: number;
  fxCrtCurvature: number;
  fxCrtScanlineIntensity: number;
  fxCrtRgbOffset: number;
  fxCrtOpacity: number;
  fxKaleidoscopeRotation: number;
  fxKaleidoscopeCenterX: number;
  fxKaleidoscopeCenterY: number;
  fxKaleidoscopeOpacity: number;
  fxPixelateOpacity: number;
  fxDitherLevels: number;
  fxDitherPixelScale: number;
  fxDitherOpacity: number;
  fxDotScreenAngle: number;
  fxDotScreenCenterX: number;
  fxDotScreenCenterY: number;
  fxDotScreenOpacity: number;
  fxFilmGrainScanlineIntensity: number;
  fxFilmGrainScanlineCount: number;
  fxFilmGrainGrayscale: number;
  fxFilmGrainOpacity: number;
  fxTiltShiftFocusPos: number;
  fxTiltShiftFocusWidth: number;
  fxTiltShiftFalloff: number;
  fxTiltShiftOpacity: number;
  fxRadialBlurCenterX: number;
  fxRadialBlurCenterY: number;
  fxRadialBlurInnerRadius: number;
  fxRadialBlurFalloff: number;
  fxRadialBlurOpacity: number;
  fxVignetteSize: number;
  fxVignetteOpacity: number;
  fxSwirlAngle: number;
  fxSwirlRadius: number;
  fxSwirlFalloff: number;
  fxSwirlCenterX: number;
  fxSwirlCenterY: number;
  fxSwirlOpacity: number;
  fxBarrelAmountX: number;
  fxBarrelAmountY: number;
  fxBarrelCenterX: number;
  fxBarrelCenterY: number;
  fxBarrelZoom: number;
  fxBarrelChroma: number;
  fxBarrelVignette: number;
  fxBarrelVignetteSize: number;
  fxBarrelOpacity: number;
  fxGridTileRows: number;
  fxGridTileGap: number;
  fxGridTileMirror: number;
  fxGridTileCenterX: number;
  fxGridTileCenterY: number;
  fxGridTileOpacity: number;
  fxSepia: number;
  fxSepiaOpacity: number;
  fxBleach: number;
  fxBleachOpacity: number;

  // -------------------------------------------------------------------------
  // Mesh Grid scene params (vizz.fm Mesh Grid parity).
  // Read by MeshGridScene; ignored by other scene types.
  // -------------------------------------------------------------------------
  /** 0 = flat plane terrain, 1 = full radial dome/bowl shape (vizz radialMix). */
  meshGridRadialMix: number;
  /** Negative = mountain (concave-up), positive = bowl (concave-down). -2..2 */
  meshGridBend: number;
  /** Mask cells outside a circle of radius half(grid). True = round terrain. */
  meshGridCircleShape: boolean;
  /** Noise displacement amplitude. 0..6 */
  meshGridNoiseAmount: number;
  /** Number of vertically-stacked terrain copies (vizz gridTile_rows). 1..5 */
  meshGridYStacks: number;
  /** Spacing between Y-stacks in world units. */
  meshGridYStackSpacing: number;
  /** Hue spread between consecutive Y-stacks (degrees). */
  meshGridYStackHueShift: number;
  /** Optional explicit grid line color (hex). Empty string = use palette hue. */
  meshGridLineColor: string;

  // -------------------------------------------------------------------------
  // Textured sphere scene params (vizz.fm `audioReactiveSphere` parity).
  // -------------------------------------------------------------------------
  /** Geometry segments (per axis). 24..256. High = petal-like. */
  sphereDetail: number;
  /** Base sphere radius in world units. 0.5..6 */
  sphereSize: number;
  /** Per-vertex displacement amplitude. 0..2 */
  sphereDisplacement: number;
  /** How much vertex displacement reacts to audio bands. 0..2 */
  sphereVertexAudioMix: number;
  /** Spatial frequency of the noise field used for displacement. 0..6 */
  sphereNoiseScale: number;
  /** Strength of the noise displacement. 0..2 */
  sphereNoiseIntensity: number;
  /** Animation speed of the noise field. 0..2 */
  sphereNoiseSpeed: number;
  /** Wave displacement frequency. 0..4 */
  sphereWaveFrequency: number;
  /** Wave displacement speed. 0..4 */
  sphereWaveSpeed: number;
  /** Color saturation/brightness multiplier. 0..4 */
  sphereColorIntensity: number;
  /** Position smoothing factor (low = jittery, high = silky). 0..1 */
  sphereMotionSmoothing: number;
  /** Continuous Y rotation speed. 0..2 */
  sphereRotationSpeed: number;
  /** Base hex color for the inner solid sphere. Empty = derive from palette. */
  sphereBaseColor: string;
  /** Accent hex color for vertex tints. Empty = lat/lon rainbow. */
  sphereAccentColor: string;
  /** Show inner dark core. */
  sphereSolidInterior: boolean;

  // -------------------------------------------------------------------------
  // Volumetric energy orb layer. Adapted from sabosugi's open-source orb
  // shader with permission; exposed as native PulseFX scene-tab recipes.
  // -------------------------------------------------------------------------
  /** World size multiplier relative to the main field radius. 0.1..0.8 */
  energyOrbSize: number;
  /** Hot/core color. */
  energyOrbPrimaryColor: string;
  /** Cooler/outer-field color. */
  energyOrbSecondaryColor: string;
  /** Volumetric emission density. 0.1..3 */
  energyOrbDensity: number;
  /** Internal time speed. 0.05..3 */
  energyOrbSpeed: number;
  /** External orb rotation. 0..1.5 */
  energyOrbRotation: number;
  /** Fractal fold iteration count. 2..8 */
  energyOrbFractalIters: number;
  /** Fractal fold scale. 0.3..1.5 */
  energyOrbFractalScale: number;
  /** Emission falloff; more negative = darker gaps. -25..-5 */
  energyOrbFractalDecay: number;
  /** Smooth fold amount. 0..0.15 */
  energyOrbSmoothness: number;
  /** Breaks mirror symmetry in the folded field. 0..1 */
  energyOrbAsymmetry: number;
  /** Additive atmosphere-shell glow. 0..5 */
  energyOrbAtmosphereGlow: number;
  /** Halo thickness. 0.1..1 */
  energyOrbAtmosphereLevel: number;
  /** Halo shell scale relative to the orb. 1..1.12 */
  energyOrbAtmosphereScale: number;
}

/**
 * Concept mode keys. Each maps to a curated set of look overrides applied
 * each frame, plus enabling/disabling specific visual layers.
 */
export type CameraMode = "orbit" | "flythrough";

export type ConceptMode =
  | "none"
  | "aurora"
  | "cathedral"
  | "tunnel"
  | "bloom"
  | "lava"
  | "nebula"
  | "warp";

export const CONCEPT_MODES: { value: ConceptMode; label: string }[] = [
  { value: "none", label: "None (manual)" },
  { value: "aurora", label: "Aurora" },
  { value: "cathedral", label: "Cathedral" },
  { value: "tunnel", label: "Tunnel" },
  { value: "bloom", label: "Bloom Drop" },
  { value: "lava", label: "Lava" },
  { value: "nebula", label: "Nebula" },
  { value: "warp", label: "Warp" },
];

export const DEFAULT_BIOCLAST: BioclastParams = {
  paletteHue: 200,
  intensity: 1.3,
  visualMode: 3,
  colorMode: 1,
  starSize: 0.18,
  saturation: 0.85,
  opacity: 1,
  atmosphere: 0.6,
  backdropBlur: 40,
  bassGravity: 1.2,
  midCurl: 1.0,
  trebleShimmer: 0.9,
  cymaticStrength: 1.6,
  flowSpeed: 0.9,
  cameraSpin: 0.6,
  cameraZoom: 1.0,
  cameraTilt: 0.55,
  cameraOrbit: 0,
  cameraMode: "orbit",
  flythroughSpeed: 1.0,
  beatDolly: 0.4,
  autopilotEnabled: false,
  autopilotInterval: 8,
  autopilotBeatLock: true,
  fieldTightness: 1.0,
  layerParticles: true,
  layerStrings: false,
  layerLattice: false,
  layerAurora: false,
  layerWarp: false,
  layerCathedral: false,
  layerDnaHelix: false,
  layerVortex: false,
  layerTide: false,
  layerOrbital: false,
  layerGalaxy: false,
  layerAuroraForest: false,
  layerBoids: false,
  layerWind: false,
  layerMercury: false,
  layerMountains: false,
  // Mesh-engine sliders shared by layerTide / layerMercury / layerMountains.
  // Surface-only for now: stored on params, exposed via the "mesh" control
  // profile; renderer wiring lands in a follow-up so changes are persisted
  // even before the visual effect is plumbed.
  meshGridWidth: 80,
  meshGridDepth: 80,
  meshCellSize: 0.1,
  meshWaveHeight: 1.0,
  meshWaveDepth: 0.5,
  meshComplexity: 0.5,
  meshHarmonics: 0.3,
  meshBeatSharpness: 0.5,
  layerEnergyOrb: false,
  layerWaveform: false,
  waveformStyle: "line",
  // Standard is the safe-everywhere default. Mobile users will be
  // auto-detected and bumped down on first launch (see auto-detect in
  // main-mobile.ts); desktop power users can manually crank to High/Ultra.
  qualityTier: "standard",
  // Default to orbital physics (the existing 19 scenes' behavior).
  // The new Emergence scenes flip particleMode to "emergence" via their
  // preset values.
  particleMode: "orbital",
  flowFormLook: "cellular-matter",
  emergenceShape: "sphere",
  mathParticleCount: 40_000,
  mathPointSize: 1,
  mathAmplitude: 0.3,
  mathFrequency: 1,
  mathRotationSpeed: 0.01,
  mathDepthScale: 0.4,
  mathWarmth: 0.62,
  mathColorSpread: 0.28,
  mathColorFlow: 0.55,
  bloomEnabled: true,
  bloomStrength: 0.85,
  bloomRadius: 0.55,
  bloomThreshold: 0.18,
  // Feedback off by default; users opt in via Settings → Trails.
  feedbackStrength: 0,
  feedbackZoom: 0,
  feedbackRotate: 0,
  feedbackDriftX: 0,
  feedbackDriftY: 0,
  feedbackDecay: 0.96,
  conceptMode: "none",

  // Camera motion (off by default — user opts in for vizz.fm-style auto-cam).
  camAutoRotateH: 0,
  camAutoRotateV: 0,
  camAutoRoll: 0,
  camZoomAmount: 0,
  camZoomPeriod: 10,
  camFovAmount: 0,
  camFovPeriod: 10,
  camPanAmount: 0,
  camPanPeriod: 10,

  // Post-FX stack defaults to enabled-but-all-zero (true passthrough).
  postFxEnabled: true,
  fxRgbShift: 0,
  fxGlitch: 0,
  fxCrt: 0,
  fxKaleidoscope: 0,
  fxPixelate: 0,
  fxDither: 0,
  fxDotScreen: 0,
  fxFilmGrain: 0,
  fxTiltShift: 0,
  fxRadialBlur: 0,
  fxVignette: 0,
  fxSwirl: 0,
  fxBarrel: 0,
  fxKaleidoscopeSegments: 6,
  fxGridTile: 0,
  fxGridTileColumns: 2,

  // Extended post-fx defaults — sensible neutrals so existing presets unaffected.
  fxRgbShiftAngle: 0,
  fxRgbShiftOpacity: 1,
  fxGlitchSpeed: 1,
  fxGlitchBlock: 1,
  fxGlitchOpacity: 1,
  fxCrtCurvature: 1,
  fxCrtScanlineIntensity: 1,
  fxCrtRgbOffset: 0,
  fxCrtOpacity: 1,
  fxKaleidoscopeRotation: 0,
  fxKaleidoscopeCenterX: 0.5,
  fxKaleidoscopeCenterY: 0.5,
  fxKaleidoscopeOpacity: 1,
  fxPixelateOpacity: 1,
  fxDitherLevels: 6,
  fxDitherPixelScale: 1,
  fxDitherOpacity: 1,
  fxDotScreenAngle: 0,
  fxDotScreenCenterX: 0.5,
  fxDotScreenCenterY: 0.5,
  fxDotScreenOpacity: 1,
  fxFilmGrainScanlineIntensity: 0,
  fxFilmGrainScanlineCount: 800,
  fxFilmGrainGrayscale: 0,
  fxFilmGrainOpacity: 1,
  fxTiltShiftFocusPos: 0.5,
  fxTiltShiftFocusWidth: 0.25,
  fxTiltShiftFalloff: 1,
  fxTiltShiftOpacity: 1,
  fxRadialBlurCenterX: 0.5,
  fxRadialBlurCenterY: 0.5,
  fxRadialBlurInnerRadius: 0,
  fxRadialBlurFalloff: 1,
  fxRadialBlurOpacity: 1,
  fxVignetteSize: 1,
  fxVignetteOpacity: 1,
  fxSwirlAngle: 1,
  fxSwirlRadius: 0.5,
  fxSwirlFalloff: 1,
  fxSwirlCenterX: 0.5,
  fxSwirlCenterY: 0.5,
  fxSwirlOpacity: 1,
  fxBarrelAmountX: 1,
  fxBarrelAmountY: 1,
  fxBarrelCenterX: 0.5,
  fxBarrelCenterY: 0.5,
  fxBarrelZoom: 1,
  fxBarrelChroma: 0,
  fxBarrelVignette: 0,
  fxBarrelVignetteSize: 1,
  fxBarrelOpacity: 1,
  fxGridTileRows: 0,
  fxGridTileGap: 0,
  fxGridTileMirror: 0,
  fxGridTileCenterX: 0.5,
  fxGridTileCenterY: 0.5,
  fxGridTileOpacity: 1,
  fxSepia: 0,
  fxSepiaOpacity: 1,
  fxBleach: 0,
  fxBleachOpacity: 1,

  meshGridRadialMix: 0,
  meshGridBend: 0,
  meshGridCircleShape: false,
  meshGridNoiseAmount: 1.5,
  meshGridYStacks: 1,
  meshGridYStackSpacing: 14,
  meshGridYStackHueShift: 0,
  meshGridLineColor: "",

  // Textured sphere defaults (matches vizz Config 28 baseline closely).
  sphereDetail: 96,
  sphereSize: 1.6,
  sphereDisplacement: 0.6,
  sphereVertexAudioMix: 0.45,
  sphereNoiseScale: 2.5,
  sphereNoiseIntensity: 0.5,
  sphereNoiseSpeed: 0.4,
  sphereWaveFrequency: 0.5,
  sphereWaveSpeed: 0,
  sphereColorIntensity: 1.5,
  sphereMotionSmoothing: 0.7,
  sphereRotationSpeed: 0,
  sphereBaseColor: "",
  sphereAccentColor: "",
  sphereSolidInterior: true,

  energyOrbSize: 0.36,
  energyOrbPrimaryColor: "#ffd36a",
  energyOrbSecondaryColor: "#ff2a00",
  energyOrbDensity: 1.8,
  energyOrbSpeed: 0.55,
  energyOrbRotation: 0.42,
  energyOrbFractalIters: 4,
  energyOrbFractalScale: 0.78,
  energyOrbFractalDecay: -14.5,
  energyOrbSmoothness: 0.018,
  energyOrbAsymmetry: 0.45,
  energyOrbAtmosphereGlow: 0.28,
  energyOrbAtmosphereLevel: 0.95,
  energyOrbAtmosphereScale: 1.035,
};

interface CymaticRing {
  age: number; // seconds since birth
  power: number; // 0..2
}

export interface BioclastDiagnostics {
  particleCapacity: number;
  particleUpdateComponents: number;
  resetEpoch: number;
  coreRingSpawns: number;
  vortexRingSpawns: number;
  mercuryRippleSpawns: number;
  windFlips: number;
}

/**
 * Quality tier — drives particle counts across every layer. Higher tiers
 * scale CPU + GPU cost roughly linearly. Persisted across sessions and
 * exposed as a UI dropdown so users can dial in for their device.
 */
export type QualityTier = "mobile" | "standard" | "high" | "ultra" | "lab";

export type FlowFormLookId =
  | "cellular-matter"
  | "neural-field"
  | "infinite-petri"
  | "coherent-fold"
  | "cymatic-current"
  | "scale-lattice"
  | "deep-field"
  | "ramsey-flow";

/** Particle multipliers per tier (relative to the "standard" baseline). */
export const QUALITY_MULT: Record<QualityTier, number> = {
  mobile: 0.25,
  standard: 1.0,
  high: 2.0,
  ultra: 4.0,
  // Lab tier targets ~1M active particles. Requires a high-end desktop GPU.
  lab: 1_000_000 / 60_000,
};

const PARTICLE_COUNT_MAX = 1_000_000;
const PARTICLE_COUNT_STANDARD = 60_000; // baseline reference for tier math

export function particleCountForTier(tier: QualityTier): number {
  return Math.min(
    PARTICLE_COUNT_MAX,
    Math.round(PARTICLE_COUNT_STANDARD * QUALITY_MULT[tier]),
  );
}

function fixedLayerCount(maxCount: number, tier: QualityTier): number {
  const multiplier = tier === "mobile" ? 0.4 : 1;
  return Math.max(1, Math.round(maxCount * multiplier));
}

const STRING_STRANDS = 36;
const STRING_STEPS = 28;
const LATTICE_RINGS = 14;
const LATTICE_SPOKES = 36;
const RADIUS = 900;

const ENERGY_ORB_VERTEX_SHADER = `
  varying vec3 vLocalPosition;
  varying vec3 vNormal;
  varying vec3 vViewPosition;

  void main() {
    vLocalPosition = position;
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const ENERGY_ORB_FRAGMENT_SHADER = `
  uniform float uTime;
  uniform vec3 uLocalCamPos;
  uniform vec3 uPrimaryColor;
  uniform vec3 uSecondaryColor;
  uniform float uDensity;
  uniform float uFractalIters;
  uniform float uFractalScale;
  uniform float uFractalDecay;
  uniform float uInternalAnim;
  uniform float uSmoothness;
  uniform float uAsymmetry;
  uniform float uBeat;
  uniform float uIntensity;

  varying vec3 vLocalPosition;
  varying vec3 vNormal;
  varying vec3 vViewPosition;

  float evaluateStructure(vec3 pos) {
    float densityAcc = 0.0;
    vec3 anchor = pos;
    float animTime = uTime * uInternalAnim;
    float s = sin(animTime);
    float c = cos(animTime);
    mat2 rotAnim = mat2(c, s, -s, c);
    float a = 0.5 * uAsymmetry;
    mat2 rotAsym1 = mat2(cos(a), sin(a), -sin(a), cos(a));
    float b = 0.3 * uAsymmetry;
    mat2 rotAsym2 = mat2(cos(b), sin(b), -sin(b), cos(b));

    for (int step = 0; step < 12; ++step) {
      if (float(step) >= uFractalIters) break;
      pos.xy *= rotAnim;
      pos.yz *= rotAnim;
      pos.xz *= rotAsym1;
      pos.yz *= rotAsym2;
      pos += vec3(0.05, -0.02, 0.03) * uAsymmetry;

      vec3 foldedPos = sqrt(pos * pos + uSmoothness);
      float magnitudeSq = max(dot(foldedPos, foldedPos), 0.00001);
      pos = (uFractalScale * foldedPos / magnitudeSq) - uFractalScale;

      float ySq = pos.y * pos.y;
      float zSq = pos.z * pos.z;
      float yz2 = 2.0 * pos.y * pos.z;
      pos.yz = vec2(ySq - zSq, yz2);
      pos = vec3(pos.z, pos.x, pos.y);
      densityAcc += exp(uFractalDecay * abs(dot(pos, anchor)));
    }

    return densityAcc * 0.5;
  }

  vec2 getVolumeBounds(vec3 origin, vec3 dir, float radius) {
    float b = dot(origin, dir);
    float c = dot(origin, origin) - radius * radius;
    float discriminant = b * b - c;
    if (discriminant < 0.0) return vec2(-1.0);
    float root = sqrt(discriminant);
    return vec2(-b - root, -b + root);
  }

  vec3 traceEnergy(vec3 origin, vec3 dir, vec2 limits) {
    float currentDepth = limits.x;
    float marchStep = 0.025;
    vec3 finalEnergy = vec3(0.0);
    float fieldVal = 0.0;

    for (int i = 0; i < 48; i++) {
      currentDepth += marchStep * exp(-2.0 * fieldVal);
      if (currentDepth > limits.y) break;

      vec3 samplePoint = origin + currentDepth * dir;
      fieldVal = evaluateStructure(samplePoint);
      float vSq = fieldVal * fieldVal;
      float gradientBlend = smoothstep(0.0, 0.4, fieldVal);
      vec3 currentGradient = mix(uSecondaryColor, uPrimaryColor, gradientBlend);
      vec3 emission = currentGradient * (fieldVal * 1.8 + vSq) * uIntensity;
      finalEnergy = 0.985 * finalEnergy + (0.052 * uDensity) * emission;
    }

    return finalEnergy;
  }

  void main() {
    vec3 rayOrig = uLocalCamPos;
    vec3 rayDir = normalize(vLocalPosition - uLocalCamPos);
    float t = uTime * 0.1;
    float s = sin(t);
    float c = cos(t);
    mat2 rotXZ = mat2(c, s, -s, c);
    rayOrig.xz *= rotXZ;
    rayDir.xz *= rotXZ;

    vec2 limits = getVolumeBounds(rayOrig, rayDir, 2.0);
    if (limits.x < 0.0) discard;

    vec3 volumeColor = traceEnergy(rayOrig, rayDir, limits);
    vec3 normal = normalize(vNormal);
    vec3 viewDir = normalize(vViewPosition);
    float facingRatio = max(dot(normal, viewDir), 0.0);
    float edgeAA = smoothstep(0.0, 0.05, facingRatio);

    vec3 finalColor = (0.42 + uBeat * 0.1) * log(1.0 + volumeColor);
    finalColor = clamp(finalColor, 0.0, 1.0) * edgeAA;

    float maxLuma = max(finalColor.r, max(finalColor.g, finalColor.b));
    float alpha = clamp(maxLuma * (1.25 + uBeat * 0.35), 0.0, 1.0) * edgeAA;
    gl_FragColor = vec4(finalColor, alpha);
  }
`;

const ENERGY_ORB_ATMOSPHERE_VERTEX_SHADER = `
  varying vec3 vNormal;
  varying vec3 vViewPosition;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const ENERGY_ORB_ATMOSPHERE_FRAGMENT_SHADER = `
  uniform vec3 uColor;
  uniform float uGlow;
  uniform float uLevel;
  uniform float uBeat;

  varying vec3 vNormal;
  varying vec3 vViewPosition;

  void main() {
    vec3 normal = normalize(vNormal);
    vec3 viewDir = normalize(vViewPosition);
    float vdn = max(dot(normal, viewDir), 0.0);
    float edgeFade = smoothstep(0.0, 0.15, vdn);
    float innerFadePoint = clamp(1.0 - uLevel, 0.0, 0.99);
    float centerFade = smoothstep(1.0, innerFadePoint, vdn);
    float alpha = edgeFade * centerFade * uGlow * (1.0 + uBeat * 0.75);
    gl_FragColor = vec4(uColor, alpha);
  }
`;

function hash(seed: number): number {
  const v = Math.sin(seed) * 43758.5453123;
  return v - Math.floor(v);
}

export class BioclastScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;

  params: BioclastParams = { ...DEFAULT_BIOCLAST };

  private features: AudioFeatures = { ...ZERO_FEATURES };
  private bassEnv = 0;
  private midEnv = 0;
  private trebEnv = 0;
  private beatEnv = 0;
  private beatEdge = false;
  private trebleEdge = false;
  private lastBeatCount = 0;
  private beatSignalHigh = false;
  private trebleSignalHigh = false;
  private resetEpoch = 0;
  private coreRingSpawns = 0;
  private vortexRingSpawns = 0;
  private mercuryRippleSpawns = 0;
  private windFlips = 0;
  private lastParticleUpdateComponents = 0;
  private rings: CymaticRing[] = [];
  private elapsed = 0;
  private orbit = 0;
  private coreFlowPhase = 0;
  private coreFlowPhaseY = 0;
  private stringsPhase = 0;
  private stringsDriftPhase = 0;
  private warpTravel = 0;
  private dnaPhase = 0;
  private vortexPhase = 0;
  private readonly orbitalPhases = new Float64Array(3);
  private galaxyRotation = 0;
  private galaxyTravel = 0;
  /** Accumulated flythrough time, scaled by params.flythroughSpeed each frame. */
  private flyTime = 0;
  public cameraModeOverride: CameraMode | null = null;
  private flythroughEye = new THREE.Vector3();

  // Particles
  private particleCapacity = PARTICLE_COUNT_STANDARD;
  private posArr = new Float32Array(PARTICLE_COUNT_STANDARD * 3);
  private velArr = new Float32Array(PARTICLE_COUNT_STANDARD * 3);
  private homeArr = new Float32Array(PARTICLE_COUNT_STANDARD * 3);
  private lifeArr = new Float32Array(PARTICLE_COUNT_STANDARD);
  private colorArr = new Float32Array(PARTICLE_COUNT_STANDARD * 3);
  private particleGeo = new THREE.BufferGeometry();
  private particles: THREE.Points;

  // Structures
  private strings: THREE.LineSegments;
  private stringPos: Float32Array;
  private lattice: THREE.LineSegments;
  private latticePos: Float32Array;

  // Aurora ribbon
  private aurora: THREE.LineSegments;
  private auroraPos: Float32Array;
  private static readonly AURORA_SEGMENTS = 360;

  // Warp tunnel rings
  private warp: THREE.LineSegments;
  private warpPos: Float32Array;
  private static readonly WARP_SEGMENTS = 480;

  // Cathedral pillars (vertical line columns + arch crowns)
  private cathedral: THREE.LineSegments;
  private cathedralPos: Float32Array;
  private static readonly CATHEDRAL_BAYS = 14;
  private static readonly CATHEDRAL_COLUMNS = 6;
  private static readonly CATHEDRAL_SEGMENTS =
    BioclastScene.CATHEDRAL_BAYS *
    (BioclastScene.CATHEDRAL_COLUMNS * 2 + BioclastScene.CATHEDRAL_COLUMNS - 1);

  // ─── New v2 scene layers ──────────────────────────────────────────────

  // DNA Helix — two strands + cross-rungs, camera-parented forward axis
  private dnaPoints: THREE.Points;
  private dnaPos: Float32Array;
  private dnaCol: Float32Array;
  private dnaRungs: THREE.LineSegments;
  private dnaRungsPos: Float32Array;
  private static readonly DNA_TURNS = 14;     // full helical turns visible
  private static readonly DNA_PER_TURN = 36;  // particles per turn per strand
  private static readonly DNA_STRANDS = 2;
  private static readonly DNA_COUNT =
    BioclastScene.DNA_TURNS *
    BioclastScene.DNA_PER_TURN *
    BioclastScene.DNA_STRANDS;
  private static readonly DNA_RUNGS_PER_TURN = 12;
  private static readonly DNA_RUNG_SEGMENTS =
    BioclastScene.DNA_TURNS * BioclastScene.DNA_RUNGS_PER_TURN;

  // Pulse Vortex — particles spiraling inward toward central event horizon
  private vortex: THREE.Points;
  private vortexPos: Float32Array;
  private vortexCol: Float32Array;
  // Per-particle phase + radius offset stored in lifeArr-style scratch.
  private vortexSeed: Float32Array;
  private static readonly VORTEX_COUNT = 55_000;
  // Concentric pulse-ring meshes that emanate from center on beats.
  private vortexRings: THREE.Mesh[] = [];
  private vortexRingState: { age: number; power: number; live: boolean }[] = [];
  private static readonly VORTEX_RING_COUNT = 5;

  // Audio Tide — 3D height-field grid deformed by live FFT
  private tide: THREE.LineSegments;
  private tidePos: Float32Array;
  private tideCol: Float32Array;
  private static readonly TIDE_W = 220; // grid columns (== FFT bins displayed)
  private static readonly TIDE_H = 130; // grid rows (depth) — deeper = more flythrough room
  // Wireframe = horizontal lines (W*H) + vertical lines ((W-1)*H + (H-1)*W).
  // Use just the horizontal ridge lines + skipped vertical struts to keep
  // the mesh light enough for mobile.
  private static readonly TIDE_SEGMENTS =
    BioclastScene.TIDE_W * BioclastScene.TIDE_H + // horizontal ridges
    BioclastScene.TIDE_W * BioclastScene.TIDE_H;  // vertical struts (sparse, see updateTide)
  // FFT band history rows so the wave actually scrolls forward in time.
  private tideHistory: Float32Array;

  // Orbital Rings — 3 concentric particle bands, each tied to a freq band
  private orbital: THREE.Points;
  private orbitalPos: Float32Array;
  private orbitalCol: Float32Array;
  private orbitalSeed: Float32Array;
  private static readonly ORBITAL_RING_COUNT = 3;
  private static readonly ORBITAL_PER_RING = 600;
  private static readonly ORBITAL_COUNT =
    BioclastScene.ORBITAL_RING_COUNT * BioclastScene.ORBITAL_PER_RING;

  // Galaxy Flythrough — spiral-arm star field with streaking warp star
  // particles for the foreground. Two layers so we can tune each part:
  //   1. galaxy disc particles (slow, dense, hue gradient by radius)
  //   2. streak particles that flow past the camera at warp speed
  private galaxyDisc: THREE.Points;
  private galaxyDiscPos: Float32Array;
  private galaxyDiscCol: Float32Array;
  private galaxyDiscSeed: Float32Array;
  private static readonly GALAXY_DISC_COUNT = 80_000;
  private static readonly GALAXY_ARMS = 4;
  private galaxyStreaks: THREE.LineSegments;
  private galaxyStreaksPos: Float32Array;
  private galaxyStreaksCol: Float32Array;
  private galaxyStreaksSeed: Float32Array;
  private static readonly GALAXY_STREAK_COUNT = 1200;

  // Aurora Forest — vertical glowing ribbons you fly through, like a forest
  // of dancing northern lights. Each ribbon is a tall thin curtain of
  // particles that sways with mid/treble.
  private auroraForest: THREE.Points;
  private auroraForestPos: Float32Array;
  private auroraForestCol: Float32Array;
  private auroraForestSeed: Float32Array;
  private static readonly AURORA_FOREST_RIBBONS = 14;
  private static readonly AURORA_FOREST_PER_RIBBON = 800;
  private static readonly AURORA_FOREST_COUNT =
    BioclastScene.AURORA_FOREST_RIBBONS * BioclastScene.AURORA_FOREST_PER_RIBBON;

  // Boid Flock — swarm of points obeying separation/alignment/cohesion.
  // Each frame the boid velocity is steered by its 6 nearest neighbours
  // (sampled from a precomputed scattering, not full O(N²)). Music adds
  // "fear" (kicks tighten the flock) and "calm" (treble fans them out).
  private boids: THREE.Points;
  private boidsPos: Float32Array; // x,y,z per particle (write each frame)
  private boidsCol: Float32Array;
  private boidsState: Float32Array; // x,y,z, vx,vy,vz per particle (interleaved)
  private static readonly BOID_COUNT = 2400;

  // Particle Wind Tunnel — long tunnel of particles flowing toward camera
  // along curving streamlines. Camera-parented foreground; particles
  // wrap around the tunnel walls and reset to the far end.
  private wind: THREE.Points;
  private windPos: Float32Array;
  private windCol: Float32Array;
  private windState: Float32Array; // (x, y, z, lane) per particle
  private windFlowDir = 1; // +1 forward (toward lens), -1 backward (after beat flip)
  private windFlipDecay = 0;
  private windTravel = 0;
  private static readonly WIND_COUNT = 22_000;

  // Liquid Mercury Pool — reflective wave surface in front of the camera.
  // Implemented as a deformable plane mesh whose vertex Y heights are
  // updated each frame from the audio + per-vertex ripple sources.
  private mercury: THREE.Mesh;
  private mercuryGeo: THREE.PlaneGeometry;
  // Up to 8 active ripple sources (born on treble hits).
  private mercuryRipples: { x: number; z: number; age: number; power: number }[] = [];
  private static readonly MERCURY_SEGMENTS = 96;

  // Mountain Range — persistent terrain heightmap you fly through. Same
  // architecture as Audio Tide (wireframe + scrolling history) but the
  // heights are noise-generated peaks that don't change shape, only roll
  // toward the camera as you fly forward. Bass amplifies peak height.
  private mountains: THREE.LineSegments;
  private mountainsPos: Float32Array;
  private mountainsCol: Float32Array;
  // Cached per-cell base height (noise sampled once at startup).
  private mountainsHeight: Float32Array;
  private mountainsScrollOffset = 0;
  private static readonly MOUNTAINS_W = 220;
  private static readonly MOUNTAINS_H = 160;
  private static readonly MOUNTAINS_SEGMENTS =
    BioclastScene.MOUNTAINS_W * BioclastScene.MOUNTAINS_H * 2;

  // Volumetric energy orb — raymarched emission inside a sphere.
  private energyOrb: THREE.Mesh;
  private energyOrbAtmosphere: THREE.Mesh;
  private energyOrbMaterial: THREE.ShaderMaterial;
  private energyOrbAtmosphereMaterial: THREE.ShaderMaterial;
  private readonly energyOrbLocalCam = new THREE.Vector3();

  // Waveform overlay — MilkDrop-style audio waveform line drawn camera-locked.
  // Three style modes: "line" (straight horizontal), "dual" (left+right
  // stereo channels offset vertically), "radial" (circle around the center).
  private waveform: THREE.Line;
  private waveformPos: Float32Array;
  private waveformGeo: THREE.BufferGeometry;
  private static readonly WAVEFORM_SAMPLES = 256; // downsampled from FFT 1024

  /** Latest backdrop tint, exposed so the host (DOM) can mirror it. */
  public readonly backdrop = { r: 4, g: 7, b: 18, alpha: 0.3, blurPx: 40 };

  /** Latest beat envelope value, exposed for the HUD overlay. */
  public get beatLevel(): number {
    return this.beatEnv;
  }
  public get bassLevel(): number {
    return this.bassEnv;
  }
  public get midLevel(): number {
    return this.midEnv;
  }
  public get trebleLevel(): number {
    return this.trebEnv;
  }

  getDiagnostics(): BioclastDiagnostics {
    return {
      particleCapacity: this.particleCapacity,
      particleUpdateComponents: this.lastParticleUpdateComponents,
      resetEpoch: this.resetEpoch,
      coreRingSpawns: this.coreRingSpawns,
      vortexRingSpawns: this.vortexRingSpawns,
      mercuryRippleSpawns: this.mercuryRippleSpawns,
      windFlips: this.windFlips,
    };
  }

  private isParticleOnlyComposition(): boolean {
    const p = this.params;
    return p.layerParticles && !(
      p.layerStrings ||
      p.layerLattice ||
      p.layerAurora ||
      p.layerWarp ||
      p.layerCathedral ||
      p.layerDnaHelix ||
      p.layerVortex ||
      p.layerTide ||
      p.layerOrbital ||
      p.layerGalaxy ||
      p.layerAuroraForest ||
      p.layerBoids ||
      p.layerWind ||
      p.layerMercury ||
      p.layerMountains ||
      p.layerEnergyOrb
    );
  }

  private markCoreParticleAttributesUpdated(activeCount: number): void {
    const componentCount = Math.max(0, Math.min(this.particleCapacity, activeCount)) * 3;
    const position = this.particleGeo.attributes.position as THREE.BufferAttribute;
    const color = this.particleGeo.attributes.color as THREE.BufferAttribute;
    position.clearUpdateRanges();
    color.clearUpdateRanges();
    position.addUpdateRange(0, componentCount);
    color.addUpdateRange(0, componentCount);
    this.lastParticleUpdateComponents = componentCount;
    position.needsUpdate = true;
    color.needsUpdate = true;
  }

  private markGeometryAttributesUpdated(
    geometry: THREE.BufferGeometry,
    itemCount: number,
    attributeNames: string[],
  ): void {
    for (const name of attributeNames) {
      const attribute = geometry.attributes[name] as THREE.BufferAttribute | undefined;
      if (!attribute) continue;
      attribute.clearUpdateRanges();
      attribute.addUpdateRange(0, itemCount * attribute.itemSize);
      attribute.needsUpdate = true;
    }
  }

  private ensureCoreParticleCapacity(tier: QualityTier): number {
    const nextCapacity = particleCountForTier(tier);
    if (nextCapacity === this.particleCapacity) return nextCapacity;

    this.particleCapacity = nextCapacity;
    this.posArr = new Float32Array(nextCapacity * 3);
    this.velArr = new Float32Array(nextCapacity * 3);
    this.homeArr = new Float32Array(nextCapacity * 3);
    this.lifeArr = new Float32Array(nextCapacity);
    this.colorArr = new Float32Array(nextCapacity * 3);
    this.emergenceShapeIdx = new Float32Array(0);
    this.seedGalaxy();

    this.particleGeo.dispose();
    this.particleGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.posArr, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.particleGeo.setAttribute(
      "color",
      new THREE.BufferAttribute(this.colorArr, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.particleGeo.setDrawRange(0, nextCapacity);
    return nextCapacity;
  }

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(60, width / height, 1, 9000);
    this.camera.position.set(0, RADIUS * 1.1, RADIUS * 1.6);
    this.camera.lookAt(0, 0, 0);

    this.scene.fog = new THREE.FogExp2(0x010104, 0.00022);

    // Background is delivered by the DOM #backdrop element behind the canvas
    // (see main.ts / main-mobile.ts). The renderer clears to near-black, and
    // the DOM gradient pulses with the music. We intentionally do NOT add an
    // in-scene background sphere here — under the bloom + tonemap pipeline,
    // a large low-poly sphere bleeds visible polygon edges into the frame.

    // Particles
    this.seedGalaxy();
    this.particleGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.posArr, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.particleGeo.setAttribute(
      "color",
      new THREE.BufferAttribute(this.colorArr, 3).setUsage(THREE.DynamicDrawUsage),
    );
    const particleMat = new THREE.PointsMaterial({
      size: 4,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.particles = new THREE.Points(this.particleGeo, particleMat);
    this.particles.frustumCulled = false;
    this.scene.add(this.particles);

    // Strings
    this.stringPos = new Float32Array(STRING_STRANDS * (STRING_STEPS - 1) * 6);
    const stringGeo = new THREE.BufferGeometry();
    stringGeo.setAttribute("position", new THREE.BufferAttribute(this.stringPos, 3));
    const stringMat = new THREE.LineBasicMaterial({
      color: 0x80d8ff,
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.strings = new THREE.LineSegments(stringGeo, stringMat);
    this.strings.frustumCulled = false;
    this.scene.add(this.strings);

    // Lattice
    const latticeSegments =
      LATTICE_RINGS * LATTICE_SPOKES + (LATTICE_RINGS - 1) * LATTICE_SPOKES;
    this.latticePos = new Float32Array(latticeSegments * 6);
    const latticeGeo = new THREE.BufferGeometry();
    latticeGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.latticePos, 3),
    );
    const latticeMat = new THREE.LineBasicMaterial({
      color: 0x5fffe2,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.lattice = new THREE.LineSegments(latticeGeo, latticeMat);
    this.lattice.frustumCulled = false;
    this.scene.add(this.lattice);

    // Aurora ribbon
    this.auroraPos = new Float32Array(BioclastScene.AURORA_SEGMENTS * 6);
    const auroraGeo = new THREE.BufferGeometry();
    auroraGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.auroraPos, 3),
    );
    const auroraMat = new THREE.LineBasicMaterial({
      color: 0x7fffe5,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.aurora = new THREE.LineSegments(auroraGeo, auroraMat);
    this.aurora.frustumCulled = false;
    this.scene.add(this.aurora);

    // Warp tunnel — parented to the CAMERA, not the scene root.
    // The geometry is built in camera-local space (extending along the
    // camera's local -Z axis), so the tunnel always appears to extend
    // straight ahead of the lens regardless of camera mode or orientation.
    // Without this, orbit-mode auto-spin would expose the tunnel from the
    // side, which just reads as a chaotic line cluster.
    this.warpPos = new Float32Array(BioclastScene.WARP_SEGMENTS * 6);
    const warpGeo = new THREE.BufferGeometry();
    warpGeo.setAttribute("position", new THREE.BufferAttribute(this.warpPos, 3));
    const warpMat = new THREE.LineBasicMaterial({
      color: 0xbfdcff,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.warp = new THREE.LineSegments(warpGeo, warpMat);
    this.warp.frustumCulled = false;
    this.camera.add(this.warp);
    // Camera must be in the scene graph for its children to render.
    this.scene.add(this.camera);

    // Cathedral pillars — parented to the CAMERA, same trick as the warp
    // tunnel. The pillars are zero-thickness lines, so any side-on view
    // shows nothing but a chaotic grid of needles. Building geometry in
    // camera-local space (extending forward along -Z) makes the colonnade
    // always read as "looking down the nave" regardless of camera mode.
    this.cathedralPos = new Float32Array(BioclastScene.CATHEDRAL_SEGMENTS * 6);
    const cathedralGeo = new THREE.BufferGeometry();
    cathedralGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.cathedralPos, 3),
    );
    const cathedralMat = new THREE.LineBasicMaterial({
      color: 0xd5f8ff,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.cathedral = new THREE.LineSegments(cathedralGeo, cathedralMat);
    this.cathedral.frustumCulled = false;
    this.scene.add(this.cathedral);

    // ─── DNA Helix ───────────────────────────────────────────────────────
    // Two strands of glowing particles spiraling around the camera's
    // forward axis. Cross-rungs (one set per turn) flash on every beat
    // like base pairs. Parented to the camera so the helix always extends
    // straight ahead of the lens regardless of camera mode.
    this.dnaPos = new Float32Array(BioclastScene.DNA_COUNT * 3);
    this.dnaCol = new Float32Array(BioclastScene.DNA_COUNT * 3);
    const dnaGeo = new THREE.BufferGeometry();
    dnaGeo.setAttribute("position", new THREE.BufferAttribute(this.dnaPos, 3));
    dnaGeo.setAttribute("color", new THREE.BufferAttribute(this.dnaCol, 3));
    const dnaMat = new THREE.PointsMaterial({
      size: 8,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.dnaPoints = new THREE.Points(dnaGeo, dnaMat);
    this.dnaPoints.frustumCulled = false;
    this.scene.add(this.dnaPoints);

    // Cross-rungs (line segments connecting the two strands).
    this.dnaRungsPos = new Float32Array(BioclastScene.DNA_RUNG_SEGMENTS * 6);
    const dnaRungsGeo = new THREE.BufferGeometry();
    dnaRungsGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.dnaRungsPos, 3),
    );
    const dnaRungsMat = new THREE.LineBasicMaterial({
      color: 0xffd56a,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.dnaRungs = new THREE.LineSegments(dnaRungsGeo, dnaRungsMat);
    this.dnaRungs.frustumCulled = false;
    this.scene.add(this.dnaRungs);

    // ─── Pulse Vortex ────────────────────────────────────────────────────
    // Particle disc spiraling inward toward a central event horizon.
    // Beats inject a radial impulse + spawn concentric shockwave rings.
    // Lives in WORLD space (not camera-parented) so it's a self-contained
    // mass that the orbit camera can frame from any angle.
    this.vortexPos = new Float32Array(BioclastScene.VORTEX_COUNT * 3);
    this.vortexCol = new Float32Array(BioclastScene.VORTEX_COUNT * 3);
    this.vortexSeed = new Float32Array(BioclastScene.VORTEX_COUNT * 3);
    for (let i = 0; i < BioclastScene.VORTEX_COUNT; i++) {
      // (radius0, theta0, vertical wobble seed) — shape stays stable
      // frame-to-frame; only theta accumulates with time.
      // Distribution biased toward the middle (pow 0.5) so the disc reads
      // as a cohesive mass rather than a thin ring near the rim.
      const i3 = i * 3;
      this.vortexSeed[i3] = 0.18 + Math.pow(hash(i * 1.31), 0.5) * 0.78;
      this.vortexSeed[i3 + 1] = hash(i * 7.71) * Math.PI * 2;
      this.vortexSeed[i3 + 2] = hash(i * 3.17);
    }
    const vortexGeo = new THREE.BufferGeometry();
    vortexGeo.setAttribute("position", new THREE.BufferAttribute(this.vortexPos, 3));
    vortexGeo.setAttribute("color", new THREE.BufferAttribute(this.vortexCol, 3));
    const vortexMat = new THREE.PointsMaterial({
      size: 6,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.vortex = new THREE.Points(vortexGeo, vortexMat);
    this.vortex.frustumCulled = false;
    this.scene.add(this.vortex);

    // Pulse-ring meshes (large flat rings expanding on beats).
    for (let i = 0; i < BioclastScene.VORTEX_RING_COUNT; i++) {
      const ringGeo = new THREE.RingGeometry(0.97, 1, 128, 1);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xff70d4,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(ringGeo, ringMat);
      mesh.rotation.x = Math.PI / 2;
      mesh.frustumCulled = false;
      this.scene.add(mesh);
      this.vortexRings.push(mesh);
      this.vortexRingState.push({ age: 99, power: 0, live: false });
    }

    // ─── Audio Tide ──────────────────────────────────────────────────────
    // 3D height-field grid that ripples in real time from the live FFT.
    // History rows scroll forward each frame so the surface flows toward
    // the camera, like watching the spectrum from above an ocean.
    this.tidePos = new Float32Array(BioclastScene.TIDE_SEGMENTS * 6);
    this.tideCol = new Float32Array(BioclastScene.TIDE_SEGMENTS * 6);
    this.tideHistory = new Float32Array(BioclastScene.TIDE_W * BioclastScene.TIDE_H);
    const tideGeo = new THREE.BufferGeometry();
    tideGeo.setAttribute("position", new THREE.BufferAttribute(this.tidePos, 3));
    tideGeo.setAttribute("color", new THREE.BufferAttribute(this.tideCol, 3));
    const tideMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.tide = new THREE.LineSegments(tideGeo, tideMat);
    this.tide.frustumCulled = false;
    // Lives in world space below the camera — the scene preset frames it
    // from above at a glancing angle so the surface reads as terrain.
    this.scene.add(this.tide);

    // ─── Orbital Rings ───────────────────────────────────────────────────
    // Three concentric particle bands at different radii, each tied to
    // bass / mid / treble. Rings precess + tilt slowly. Self-contained
    // mass framed by an orbit camera.
    this.orbitalPos = new Float32Array(BioclastScene.ORBITAL_COUNT * 3);
    this.orbitalCol = new Float32Array(BioclastScene.ORBITAL_COUNT * 3);
    this.orbitalSeed = new Float32Array(BioclastScene.ORBITAL_COUNT * 2);
    for (let i = 0; i < BioclastScene.ORBITAL_COUNT; i++) {
      // (theta0, vertical jitter) — radius is derived from ring index.
      this.orbitalSeed[i * 2] = hash(i * 11.13) * Math.PI * 2;
      this.orbitalSeed[i * 2 + 1] = (hash(i * 4.7) - 0.5) * 0.18;
    }
    const orbitalGeo = new THREE.BufferGeometry();
    orbitalGeo.setAttribute("position", new THREE.BufferAttribute(this.orbitalPos, 3));
    orbitalGeo.setAttribute("color", new THREE.BufferAttribute(this.orbitalCol, 3));
    const orbitalMat = new THREE.PointsMaterial({
      size: 7,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.orbital = new THREE.Points(orbitalGeo, orbitalMat);
    this.orbital.frustumCulled = false;
    this.scene.add(this.orbital);

    // ─── Galaxy Flythrough ──────────────────────────────────────────────
    // 80k particles distributed across N spiral arms in a flat-ish disc
    // with a brighter core. Plus 1.2k streak segments that flow past the
    // camera at warp speed for the "flying through stars" foreground.
    // Lives in WORLD space so the scene preset can position the camera
    // inside the disc looking outward into a spiral arm.
    this.galaxyDiscPos = new Float32Array(BioclastScene.GALAXY_DISC_COUNT * 3);
    this.galaxyDiscCol = new Float32Array(BioclastScene.GALAXY_DISC_COUNT * 3);
    // Per-particle (radius0, armIdx, angularJitter, verticalJitter).
    this.galaxyDiscSeed = new Float32Array(BioclastScene.GALAXY_DISC_COUNT * 4);
    for (let i = 0; i < BioclastScene.GALAXY_DISC_COUNT; i++) {
      const i4 = i * 4;
      // Density biased toward the core (pow 0.6).
      const r0 = Math.pow(hash(i * 1.31), 0.6);
      const arm = Math.floor(hash(i * 7.71) * BioclastScene.GALAXY_ARMS);
      // Angular scatter around the spiral arm, tighter near the core.
      const angJitter = (hash(i * 3.17) - 0.5) * 0.45 * (0.4 + r0 * 0.6);
      // Vertical scatter — disc is flat near edge, puffy in core.
      const yJitter = (hash(i * 11.7) - 0.5) * (0.05 + (1 - r0) * 0.18);
      this.galaxyDiscSeed[i4] = r0;
      this.galaxyDiscSeed[i4 + 1] = arm;
      this.galaxyDiscSeed[i4 + 2] = angJitter;
      this.galaxyDiscSeed[i4 + 3] = yJitter;
    }
    const galaxyDiscGeo = new THREE.BufferGeometry();
    galaxyDiscGeo.setAttribute("position", new THREE.BufferAttribute(this.galaxyDiscPos, 3));
    galaxyDiscGeo.setAttribute("color", new THREE.BufferAttribute(this.galaxyDiscCol, 3));
    const galaxyDiscMat = new THREE.PointsMaterial({
      size: 4,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.galaxyDisc = new THREE.Points(galaxyDiscGeo, galaxyDiscMat);
    this.galaxyDisc.frustumCulled = false;
    this.scene.add(this.galaxyDisc);

    // Galaxy streak overlay — short bright line segments in world space,
    // streaming through the disc without being parented to the camera.
    this.galaxyStreaksPos = new Float32Array(BioclastScene.GALAXY_STREAK_COUNT * 6);
    this.galaxyStreaksCol = new Float32Array(BioclastScene.GALAXY_STREAK_COUNT * 6);
    // Per-streak (azimuth, elevation, depthPhase) — depthPhase advances
    // with time and wraps to give the perpetual-stream effect.
    this.galaxyStreaksSeed = new Float32Array(BioclastScene.GALAXY_STREAK_COUNT * 3);
    for (let i = 0; i < BioclastScene.GALAXY_STREAK_COUNT; i++) {
      const i3 = i * 3;
      this.galaxyStreaksSeed[i3] = hash(i * 5.13) * Math.PI * 2;
      this.galaxyStreaksSeed[i3 + 1] = (hash(i * 9.7) - 0.5) * 0.6;
      this.galaxyStreaksSeed[i3 + 2] = hash(i * 2.31);
    }
    const galaxyStreaksGeo = new THREE.BufferGeometry();
    galaxyStreaksGeo.setAttribute("position", new THREE.BufferAttribute(this.galaxyStreaksPos, 3));
    galaxyStreaksGeo.setAttribute("color", new THREE.BufferAttribute(this.galaxyStreaksCol, 3));
    const galaxyStreaksMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.galaxyStreaks = new THREE.LineSegments(galaxyStreaksGeo, galaxyStreaksMat);
    this.galaxyStreaks.frustumCulled = false;
    this.scene.add(this.galaxyStreaks);

    // ─── Aurora Forest ──────────────────────────────────────────────────
    // N tall vertical ribbons (each ~800 particles) standing in a circle
    // around the origin. Each ribbon sways with mid + treble. The user
    // stands in the center looking out at a forest of dancing aurora.
    this.auroraForestPos = new Float32Array(BioclastScene.AURORA_FOREST_COUNT * 3);
    this.auroraForestCol = new Float32Array(BioclastScene.AURORA_FOREST_COUNT * 3);
    // Per-particle (ribbonIdx, heightFraction).
    this.auroraForestSeed = new Float32Array(BioclastScene.AURORA_FOREST_COUNT * 2);
    for (let i = 0; i < BioclastScene.AURORA_FOREST_COUNT; i++) {
      const ribbon = Math.floor(i / BioclastScene.AURORA_FOREST_PER_RIBBON);
      const within = (i % BioclastScene.AURORA_FOREST_PER_RIBBON) /
        (BioclastScene.AURORA_FOREST_PER_RIBBON - 1);
      this.auroraForestSeed[i * 2] = ribbon;
      this.auroraForestSeed[i * 2 + 1] = within;
    }
    const auroraForestGeo = new THREE.BufferGeometry();
    auroraForestGeo.setAttribute("position", new THREE.BufferAttribute(this.auroraForestPos, 3));
    auroraForestGeo.setAttribute("color", new THREE.BufferAttribute(this.auroraForestCol, 3));
    const auroraForestMat = new THREE.PointsMaterial({
      size: 6,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.auroraForest = new THREE.Points(auroraForestGeo, auroraForestMat);
    this.auroraForest.frustumCulled = false;
    this.scene.add(this.auroraForest);

    // ─── Boid Flock ─────────────────────────────────────────────────────
    // 2.4k boids in 3D. Each boid carries (pos, vel) in `boidsState`.
    // Per-frame: cheap "global" forces (cohere to center of mass, swirl,
    // separate from a few sampled neighbours) so the swarm reads as a
    // unified organism without paying O(N²) cost.
    this.boidsPos = new Float32Array(BioclastScene.BOID_COUNT * 3);
    this.boidsCol = new Float32Array(BioclastScene.BOID_COUNT * 3);
    // Interleaved (px, py, pz, vx, vy, vz) per boid.
    this.boidsState = new Float32Array(BioclastScene.BOID_COUNT * 6);
    this.seedBoids();
    const boidsGeo = new THREE.BufferGeometry();
    boidsGeo.setAttribute("position", new THREE.BufferAttribute(this.boidsPos, 3));
    boidsGeo.setAttribute("color", new THREE.BufferAttribute(this.boidsCol, 3));
    const boidsMat = new THREE.PointsMaterial({
      size: 7,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.boids = new THREE.Points(boidsGeo, boidsMat);
    this.boids.frustumCulled = false;
    this.scene.add(this.boids);

    // ─── Particle Wind Tunnel ──────────────────────────────────────────
    // 22k particles in a cylindrical tunnel parented to the camera. Each
    // particle has a (radius, theta, depthPhase) — depth advances with
    // time to flow toward the lens, wraps when it crosses.
    this.windPos = new Float32Array(BioclastScene.WIND_COUNT * 3);
    this.windCol = new Float32Array(BioclastScene.WIND_COUNT * 3);
    // Per-particle (radius0, theta0, depthPhase0, lane).
    this.windState = new Float32Array(BioclastScene.WIND_COUNT * 4);
    for (let i = 0; i < BioclastScene.WIND_COUNT; i++) {
      const i4 = i * 4;
      // Bias particles toward the tunnel walls (annular distribution)
      // so the center stays clear and you actually feel "inside" something.
      this.windState[i4] = 0.5 + Math.pow(hash(i * 1.31), 0.4) * 0.7;
      this.windState[i4 + 1] = hash(i * 7.71) * Math.PI * 2;
      this.windState[i4 + 2] = hash(i * 3.17);
      // Lane = depth-flow speed jitter, makes particles parallax.
      this.windState[i4 + 3] = 0.7 + hash(i * 11.7) * 0.6;
    }
    const windGeo = new THREE.BufferGeometry();
    windGeo.setAttribute("position", new THREE.BufferAttribute(this.windPos, 3));
    windGeo.setAttribute("color", new THREE.BufferAttribute(this.windCol, 3));
    const windMat = new THREE.PointsMaterial({
      size: 5,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: makeSpriteTexture(),
    });
    this.wind = new THREE.Points(windGeo, windMat);
    this.wind.frustumCulled = false;
    this.camera.add(this.wind);

    // ─── Liquid Mercury Pool ───────────────────────────────────────────
    // A wide horizontal plane just below the camera. Vertices are
    // displaced in Y per-frame by a sum of sine waves driven by audio
    // bands plus localized ripple sources born on treble hits.
    // Glossy material with strong specular tint so the surface reads as
    // liquid metal in the bloom pass.
    this.mercuryGeo = new THREE.PlaneGeometry(
      RADIUS * 12,
      RADIUS * 12,
      BioclastScene.MERCURY_SEGMENTS,
      BioclastScene.MERCURY_SEGMENTS,
    );
    this.mercuryGeo.rotateX(-Math.PI / 2); // lie flat
    const mercuryMat = new THREE.MeshBasicMaterial({
      color: 0xa8d4ff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.16,
      blending: THREE.NormalBlending,
      depthWrite: false,
      wireframe: true,
    });
    this.mercury = new THREE.Mesh(this.mercuryGeo, mercuryMat);
    this.mercury.position.y = -RADIUS * 0.4;
    this.mercury.frustumCulled = false;
    this.scene.add(this.mercury);

    // ─── Mountain Range ────────────────────────────────────────────────
    // Wireframe heightmap with cached noise heights — peaks stay put as
    // the camera flies forward through them. Same world-space wireframe
    // architecture as Audio Tide, but the height array is precomputed
    // and SCROLLS each frame instead of being rewritten from FFT.
    const mw = BioclastScene.MOUNTAINS_W;
    const mh = BioclastScene.MOUNTAINS_H;
    this.mountainsPos = new Float32Array(BioclastScene.MOUNTAINS_SEGMENTS * 6);
    this.mountainsCol = new Float32Array(BioclastScene.MOUNTAINS_SEGMENTS * 6);
    // Bake the terrain heights once. Multi-octave value-noise gives
    // organic-looking ridges and valleys.
    this.mountainsHeight = new Float32Array(mw * mh);
    for (let row = 0; row < mh; row++) {
      for (let col = 0; col < mw; col++) {
        const u = col / mw;
        const v = row / mh;
        // 4-octave fractal noise.
        let n = 0;
        let amp = 1;
        let freq = 1;
        let total = 0;
        for (let oct = 0; oct < 4; oct++) {
          // Cheap deterministic noise from hash() — interpolated bilinearly.
          const fx = u * freq * 6;
          const fy = v * freq * 6;
          const ix = Math.floor(fx);
          const iy = Math.floor(fy);
          const tx = fx - ix;
          const ty = fy - iy;
          const h00 = hash(ix * 12.9898 + iy * 78.233);
          const h10 = hash((ix + 1) * 12.9898 + iy * 78.233);
          const h01 = hash(ix * 12.9898 + (iy + 1) * 78.233);
          const h11 = hash((ix + 1) * 12.9898 + (iy + 1) * 78.233);
          const sx = tx * tx * (3 - 2 * tx);
          const sy = ty * ty * (3 - 2 * ty);
          const a = h00 + sx * (h10 - h00);
          const b = h01 + sx * (h11 - h01);
          n += (a + sy * (b - a)) * amp;
          total += amp;
          amp *= 0.5;
          freq *= 2;
        }
        n /= total; // 0..1
        // Carve a valley down the center so the camera always has a
        // navigable corridor (no peaks blocking the lens dead-on).
        const centerDist = Math.abs((col / (mw - 1)) - 0.5) * 2; // 0..1
        const valley = Math.max(0, 1 - centerDist * 2.5);
        const ridge = Math.pow(n, 1.4) * (1 - valley * 0.6);
        this.mountainsHeight[row * mw + col] = ridge;
      }
    }
    const mountainsGeo = new THREE.BufferGeometry();
    mountainsGeo.setAttribute("position", new THREE.BufferAttribute(this.mountainsPos, 3));
    mountainsGeo.setAttribute("color", new THREE.BufferAttribute(this.mountainsCol, 3));
    const mountainsMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.mountains = new THREE.LineSegments(mountainsGeo, mountainsMat);
    this.mountains.frustumCulled = false;
    this.scene.add(this.mountains);

    // ─── Volumetric Energy Orb ────────────────────────────────────────
    const energyOrbGeo = new THREE.SphereGeometry(2, 96, 96);
    this.energyOrbMaterial = new THREE.ShaderMaterial({
      vertexShader: ENERGY_ORB_VERTEX_SHADER,
      fragmentShader: ENERGY_ORB_FRAGMENT_SHADER,
      uniforms: {
        uTime: { value: 0 },
        uLocalCamPos: { value: new THREE.Vector3() },
        uPrimaryColor: { value: new THREE.Color(DEFAULT_BIOCLAST.energyOrbPrimaryColor) },
        uSecondaryColor: { value: new THREE.Color(DEFAULT_BIOCLAST.energyOrbSecondaryColor) },
        uDensity: { value: DEFAULT_BIOCLAST.energyOrbDensity },
        uFractalIters: { value: DEFAULT_BIOCLAST.energyOrbFractalIters },
        uFractalScale: { value: DEFAULT_BIOCLAST.energyOrbFractalScale },
        uFractalDecay: { value: DEFAULT_BIOCLAST.energyOrbFractalDecay },
        uInternalAnim: { value: DEFAULT_BIOCLAST.energyOrbSpeed },
        uSmoothness: { value: DEFAULT_BIOCLAST.energyOrbSmoothness },
        uAsymmetry: { value: DEFAULT_BIOCLAST.energyOrbAsymmetry },
        uBeat: { value: 0 },
        uIntensity: { value: DEFAULT_BIOCLAST.intensity },
      },
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.energyOrb = new THREE.Mesh(energyOrbGeo, this.energyOrbMaterial);
    this.energyOrb.frustumCulled = false;
    this.energyOrb.scale.setScalar(RADIUS * DEFAULT_BIOCLAST.energyOrbSize);
    this.scene.add(this.energyOrb);

    this.energyOrbAtmosphereMaterial = new THREE.ShaderMaterial({
      vertexShader: ENERGY_ORB_ATMOSPHERE_VERTEX_SHADER,
      fragmentShader: ENERGY_ORB_ATMOSPHERE_FRAGMENT_SHADER,
      uniforms: {
        uColor: { value: new THREE.Color(DEFAULT_BIOCLAST.energyOrbPrimaryColor) },
        uGlow: { value: DEFAULT_BIOCLAST.energyOrbAtmosphereGlow },
        uLevel: { value: DEFAULT_BIOCLAST.energyOrbAtmosphereLevel },
        uBeat: { value: 0 },
      },
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.energyOrbAtmosphere = new THREE.Mesh(energyOrbGeo, this.energyOrbAtmosphereMaterial);
    this.energyOrbAtmosphere.frustumCulled = false;
    this.energyOrbAtmosphere.scale.setScalar(DEFAULT_BIOCLAST.energyOrbAtmosphereScale);
    this.energyOrb.add(this.energyOrbAtmosphere);

    // ─── Waveform overlay ───────────────────────────────────────────────
    // Camera-locked Line that draws the live audio waveform across the
    // screen. Vertices are updated every frame from the FFT analyzer's
    // time-domain PCM samples. Three style modes (line/dual/radial) all
    // share the same buffer; the per-frame updater rewrites positions.
    // 256 sample resolution is plenty smooth at any aspect ratio.
    this.waveformPos = new Float32Array(BioclastScene.WAVEFORM_SAMPLES * 3);
    this.waveformGeo = new THREE.BufferGeometry();
    this.waveformGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(this.waveformPos, 3),
    );
    const waveformMat = new THREE.LineBasicMaterial({
      color: 0x7df9ff,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      linewidth: 2, // Note: most browsers ignore >1px in WebGL; bloom adds glow
    });
    this.waveform = new THREE.Line(this.waveformGeo, waveformMat);
    this.waveform.frustumCulled = false;
    this.waveform.renderOrder = 999; // draw last, on top of everything
    this.camera.add(this.waveform);

  }

  setFeatures(f: AudioFeatures): void {
    this.features = f;
  }

  setSize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  restartFlythroughCamera(): void {
    this.flyTime = 0;
  }

  resetState(): void {
    const activeCount = this.ensureCoreParticleCapacity(this.params.qualityTier);
    this.seedGalaxy();
    this.emergenceShapeIdx = new Float32Array(0);
    this.particleGeo.setDrawRange(0, activeCount);
    this.markCoreParticleAttributesUpdated(activeCount);
    this.seedBoids();

    this.bassEnv = this.features.bass;
    this.midEnv = this.features.mid;
    this.trebEnv = this.features.treble;
    this.beatEnv = 0;
    this.beatEdge = false;
    this.trebleEdge = false;
    this.lastBeatCount = this.features.beatCount;
    this.beatSignalHigh = this.features.beat > 0.3;
    this.trebleSignalHigh = this.features.treble > 0.35;
    this.rings = [];
    this.elapsed = 0;
    this.orbit = 0;
    this.flyTime = 0;
    this.coreFlowPhase = 0;
    this.coreFlowPhaseY = 0;
    this.stringsPhase = 0;
    this.stringsDriftPhase = 0;
    this.warpTravel = 0;
    this.dnaPhase = 0;
    this.vortexPhase = 0;
    this.orbitalPhases.fill(0);
    this.galaxyRotation = 0;
    this.galaxyTravel = 0;
    this.windTravel = 0;
    this.windFlowDir = 1;
    this.windFlipDecay = 0;
    this.tideScrollElapsed = 0;
    this.tideHistory.fill(0);
    this.mercuryRipples = [];
    this.mountainsScrollOffset = 0;
    this.hideVortexRings();
    this.energyOrb.rotation.set(0, 0, 0);
    this.energyOrbMaterial.uniforms.uTime.value = 0;
    this.resetEpoch += 1;
    this.coreRingSpawns = 0;
    this.vortexRingSpawns = 0;
    this.mercuryRippleSpawns = 0;
    this.windFlips = 0;
  }

  dispose(): void {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    const textures = new Set<THREE.Texture>();

    this.scene.traverse((object) => {
      const renderable = object as THREE.Object3D & {
        geometry?: THREE.BufferGeometry;
        material?: THREE.Material | THREE.Material[];
      };
      if (renderable.geometry) geometries.add(renderable.geometry);
      const objectMaterials = Array.isArray(renderable.material)
        ? renderable.material
        : renderable.material
          ? [renderable.material]
          : [];
      for (const material of objectMaterials) {
        materials.add(material);
        for (const value of Object.values(material)) {
          if (value instanceof THREE.Texture) textures.add(value);
        }
      }
    });

    for (const texture of textures) texture.dispose();
    for (const material of materials) material.dispose();
    for (const geometry of geometries) geometry.dispose();
    this.scene.clear();
  }

  /** Per-frame update. dt is in seconds. */
  update(dt: number): void {
    this.elapsed += dt;

    // ---- Audio envelopes (smoothed) ----
    const f = this.features;
    const p = this.params;
    this.bassEnv = f.bass;
    this.midEnv = f.mid;
    this.trebEnv = f.treble;
    this.beatEnv = f.beat;

    const beatCountAdvanced = f.beatCount > this.lastBeatCount;
    const beatLevelHigh = f.beat > 0.55;
    this.beatEdge = beatCountAdvanced || (beatLevelHigh && !this.beatSignalHigh);
    this.beatSignalHigh = f.beat > 0.3;
    this.lastBeatCount = f.beatCount;
    const trebleLevelHigh = this.trebEnv > 0.55;
    this.trebleEdge = trebleLevelHigh && !this.trebleSignalHigh;
    this.trebleSignalHigh = this.trebEnv > 0.35;

    // Spawn cymatic ring on a fresh strong beat.
    if (this.beatEdge) {
      const power = Math.min(1.6, f.beat * 1.2 + this.bassEnv * 0.5);
      this.spawnRing(power);
    }
    for (const ring of this.rings) ring.age += dt;
    for (const ring of this.rings) ring.power *= Math.exp(-1.4 * dt);
    this.rings = this.rings.filter((r) => r.power > 0.02 && r.age < 6);

    const energy = (this.bassEnv + this.midEnv + this.trebEnv) / 3;

    // ---- Concept mode overrides ----
    // These tweak hue/visualMode/etc. *without* mutating params, so the user's
    // manual sliders stay intact when they switch back to "none".
    const fx = applyConceptMode(p.conceptMode, p);

    // ---- Camera ----
    // Concept overrides win: e.g. warp/tunnel only read correctly head-on.
    const effectiveCameraMode = this.cameraModeOverride ?? fx.forceCameraMode ?? p.cameraMode;
    if (effectiveCameraMode === "flythrough") {
      this.updateFlythroughCamera(dt, energy, fx);
    } else {
      // Auto-orbit: third-person camera around the field. Combines auto-spin
      // with the user's manual yaw drag, vertical tilt, and zoom slider.
      // When cameraSpin is 0, ALL beat-driven camera motion is disabled
      // (no spin, no vertical bob, no FOV pulse, no energy dolly) so
      // static framings hold rock-still — the music drives the visuals
      // *inside* the frame instead of shaking the camera.
      const isStatic = p.cameraSpin <= 0;
      const autoSpin = isStatic ? 0 : 0.05 + p.cameraSpin * (0.04 + energy * 0.08);
      this.orbit += dt * autoSpin;
      const yaw = this.orbit + p.cameraOrbit;
      const tilt = Math.max(0, Math.min(1, p.cameraTilt));
      const energyDolly = isStatic ? 0 : 0.15 * energy;
      const baseR = RADIUS * (fx.cameraDistance - energyDolly);
      const camR = baseR / Math.max(0.15, Math.min(3.5, p.cameraZoom));
      const horizR = camR * Math.cos(tilt * Math.PI * 0.5);
      this.camera.position.x = Math.cos(yaw) * horizR;
      this.camera.position.z = Math.sin(yaw) * horizR;
      const verticalBob = isStatic
        ? 0
        : RADIUS * 0.15 * Math.sin(this.elapsed * 0.3) * 0.4;
      this.camera.position.y =
        camR * Math.sin(tilt * Math.PI * 0.5) +
        RADIUS * (fx.cameraHeight - 1.0) +
        verticalBob;
      this.camera.lookAt(0, 0, 0);
      const fovPulse = isStatic ? 0 : -energy * 2 + this.beatEnv * 2.5;
      this.camera.fov = 62 + fovPulse;
      this.camera.updateProjectionMatrix();
    }

    // ---- Particle physics ----
    // Skip the per-frame physics work entirely if the particle layer is off.
    if (p.layerParticles) {
      // Dispatch to the right physics. Emergence is the new self-organizing
      // attractor system (lava-lamp / blob morphing), orbital is the
      // gravitational swirl that powers the original 19 scenes.
      if (p.particleMode === "emergence") {
        this.updateEmergence(dt, energy, fx);
      } else {
        this.updateParticles(dt, energy, fx);
      }
    }
    this.particles.visible = p.layerParticles;

    // ---- Layer visibility ----
    // Single source of truth: the per-layer toggles on `params`. The legacy
    // `visualMode` field is preserved on the params object only for backward
    // compatibility with older saved presets and the deprecated Composition
    // segmented control. Concept overrides (fx.show*) only nudge layers when
    // the user hasn't pinned them via the new toggle UI.
    this.strings.visible = p.layerStrings;
    this.lattice.visible = p.layerLattice;
    this.aurora.visible = p.layerAurora;
    this.warp.visible = p.layerWarp;
    this.cathedral.visible = p.layerCathedral;
    this.dnaPoints.visible = p.layerDnaHelix;
    this.dnaRungs.visible = p.layerDnaHelix;
    this.vortex.visible = p.layerVortex;
    this.tide.visible = p.layerTide;
    this.orbital.visible = p.layerOrbital;
    this.galaxyDisc.visible = p.layerGalaxy;
    this.galaxyStreaks.visible = p.layerGalaxy;
    this.auroraForest.visible = p.layerAuroraForest;
    this.boids.visible = p.layerBoids;
    this.wind.visible = p.layerWind;
    this.mercury.visible = p.layerMercury;
    this.mountains.visible = p.layerMountains;
    this.energyOrb.visible = p.layerEnergyOrb;
    this.waveform.visible = p.layerWaveform;

    if (this.strings.visible) this.updateStrings(dt, energy, fx);
    if (this.lattice.visible) this.updateLattice(energy, fx);
    if (this.aurora.visible) this.updateAurora(energy, fx);
    if (this.warp.visible) this.updateWarp(dt, energy, fx);
    if (this.cathedral.visible) this.updateCathedral(energy, fx);
    if (this.dnaPoints.visible) this.updateDnaHelix(dt, energy, fx);
    if (this.vortex.visible) this.updateVortex(dt, energy, fx);
    else this.hideVortexRings();
    if (this.tide.visible) this.updateTide(dt, energy, fx);
    if (this.orbital.visible) this.updateOrbital(dt, energy, fx);
    if (this.galaxyDisc.visible) this.updateGalaxy(dt, energy, fx);
    if (this.auroraForest.visible) this.updateAuroraForest(energy, fx);
    if (this.boids.visible) this.updateBoids(dt, energy, fx);
    if (this.wind.visible) this.updateWind(dt, energy, fx);
    if (this.mercury.visible) this.updateMercury(dt, energy, fx);
    if (this.mountains.visible) this.updateMountains(dt, energy, fx);
    if (this.energyOrb.visible) this.updateEnergyOrb(dt, energy, fx);
    if (this.waveform.visible) this.updateWaveform(fx);
    // Fog density modulates with energy
    const fog = this.scene.fog as THREE.FogExp2 | null;
    if (fog) fog.density = 0.00018 + energy * 0.0002 + this.beatEnv * 0.00006;

    // Particle material global params
    const pMat = this.particles.material as THREE.PointsMaterial;
    const particleOnly = this.isParticleOnlyComposition();
    pMat.size =
      Math.max(5.5, 26 * p.starSize) *
      (1 + energy * 0.6 + this.beatEnv * 0.5 + p.trebleShimmer * 0.05) *
      (particleOnly ? 1.36 + this.beatEnv * 0.44 : 1);
    // Opacity stays clamped at 1 for additive blending; brightness above 1
    // is delivered through the per-particle color (intensityBoost below).
    pMat.opacity = Math.min(
      1,
      (0.9 + energy * 0.15 + this.beatEnv * 0.1) *
        Math.max(0, Math.min(1, p.opacity)) *
        (particleOnly ? 1.1 : 1),
    );

    // ---- Backdrop tint (read by the host DOM each frame) ----
    const tintColor = paletteColor(fx.hueDeg, 0.5);
    this.backdrop.r = Math.min(255, Math.round(4 + tintColor.r * 60 + this.bassEnv * 40));
    this.backdrop.g = Math.min(255, Math.round(7 + tintColor.g * 40 + this.midEnv * 35));
    this.backdrop.b = Math.min(255, Math.round(18 + tintColor.b * 80 + this.trebEnv * 60));
    this.backdrop.alpha = Math.min(
      0.95,
      (0.28 + energy * 0.35 + this.beatEnv * 0.18) * p.atmosphere,
    );
    this.backdrop.blurPx = p.backdropBlur;
  }

  // -------------------------------------------------------------------------
  // Particles
  // -------------------------------------------------------------------------

  private seedGalaxy(): void {
    for (let i = 0; i < this.particleCapacity; i++) {
      const i3 = i * 3;
      const h0 = hash(i * 12.9898);
      const h1 = hash(i * 78.233 + 17.17);
      const h2 = hash(i * 37.719 + 91.7);
      const h3 = hash(i * 11.135 + 41.3);
      const h4 = hash(i * 63.137 + 8.91);
      const r = Math.pow(h1, 0.6);
      const angle = h0 * Math.PI * 2 + r * 4.5 + (h2 - 0.5) * 1.4;
      const radius =
        RADIUS * (0.05 + r * 0.92) + (h3 - 0.5) * RADIUS * 0.07;
      const height = (h4 - 0.5) * RADIUS * (0.05 + r * 0.18);

      const x = Math.cos(angle) * radius;
      const y = height;
      const z = Math.sin(angle) * radius;

      this.homeArr[i3] = x;
      this.homeArr[i3 + 1] = y;
      this.homeArr[i3 + 2] = z;

      this.posArr[i3] = x;
      this.posArr[i3 + 1] = y;
      this.posArr[i3 + 2] = z;

      this.velArr[i3] = -Math.sin(angle) * (0.5 + hash(i * 3.1));
      this.velArr[i3 + 1] = (hash(i * 8.2) - 0.5) * 0.05;
      this.velArr[i3 + 2] = Math.cos(angle) * (0.5 + hash(i * 4.4));

      this.lifeArr[i] = 0.8 + hash(i * 6.1) * 0.2;

      // Initial color (will be re-shaded each frame for color modes that need it).
      this.colorArr[i3] = 0.6;
      this.colorArr[i3 + 1] = 0.8;
      this.colorArr[i3 + 2] = 1.0;
    }
  }

  private seedBoids(): void {
    for (let i = 0; i < BioclastScene.BOID_COUNT; i++) {
      const i6 = i * 6;
      const theta = hash(i * 1.31) * Math.PI * 2;
      const phi = Math.acos(2 * hash(i * 5.71) - 1);
      const r = Math.cbrt(hash(i * 9.13)) * RADIUS;
      this.boidsState[i6] = Math.sin(phi) * Math.cos(theta) * r;
      this.boidsState[i6 + 1] = Math.sin(phi) * Math.sin(theta) * r * 0.7;
      this.boidsState[i6 + 2] = Math.cos(phi) * r;
      this.boidsState[i6 + 3] = (hash(i * 17.7) - 0.5) * 60;
      this.boidsState[i6 + 4] = (hash(i * 23.3) - 0.5) * 60;
      this.boidsState[i6 + 5] = (hash(i * 31.5) - 0.5) * 60;
    }
  }

  private updateParticles(dt: number, energy: number, fx: ConceptEffects): void {
    const p = this.params;
    const t = this.elapsed;
    const particleOnly = this.isParticleOnlyComposition();
    const particleOnlyBoost = particleOnly ? 1.55 : 1;
    const flow = p.flowSpeed * fx.flowMul;
    const bass = this.bassEnv * p.bassGravity * fx.bassMul * particleOnlyBoost;
    const mid = this.midEnv * p.midCurl * fx.midMul * (particleOnly ? 1.25 : 1);
    const treb = this.trebEnv * p.trebleShimmer * fx.trebMul;
    // Squeeze (tightness < 1) or fan out (> 1) the home positions. Particles
    // pull toward `home * tightness`, so the whole field can be tightened
    // without re-seeding or losing its character.
    const tightness = Math.max(0.4, Math.min(1.6, p.fieldTightness));
    this.coreFlowPhase += dt * (0.5 + flow);
    this.coreFlowPhaseY += dt * (0.4 + flow);

    // Cymatic pressure samples
    const ringForce = (rNorm: number): number => {
      let total = 0;
      for (const ring of this.rings) {
        const speed = 1.05;
        const width = 24;
        const wave = Math.exp(-Math.abs(rNorm - ring.age * speed) * width);
        total += wave * ring.power;
      }
      return total * p.cymaticStrength;
    };

    const colorMode = p.colorMode;
    const baseHue = ((fx.hueDeg / 360) % 1 + 1) % 1;

    const activeCount = this.ensureCoreParticleCapacity(p.qualityTier);
    this.particleGeo.setDrawRange(0, activeCount);

    for (let i = 0; i < activeCount; i++) {
      const i3 = i * 3;
      // Apply tightness so particles seek a tighter (or wider) home each
      // frame without re-seeding. Vertical home is squeezed less so a tight
      // field stays disc-shaped instead of becoming a sphere.
      const hx = this.homeArr[i3] * tightness;
      const hy = this.homeArr[i3 + 1] * (0.6 + tightness * 0.4);
      const hz = this.homeArr[i3 + 2] * tightness;
      let px = this.posArr[i3];
      let py = this.posArr[i3 + 1];
      let pz = this.posArr[i3 + 2];
      let vx = this.velArr[i3];
      let vy = this.velArr[i3 + 1];
      let vz = this.velArr[i3 + 2];

      const dist = Math.max(1, Math.hypot(px, py, pz));
      const dirX = px / dist;
      const dirZ = pz / dist;

      const rNorm = dist / RADIUS;
      const cym = ringForce(rNorm);

      // Curl-ish swirl from sin/cos field (cheap)
      const swirlX =
        Math.sin(py * 0.004 + this.coreFlowPhase) * (40 + 60 * mid + 80 * energy);
      const swirlY =
        Math.cos(pz * 0.004 + this.coreFlowPhaseY) * (20 + 50 * mid + 40 * energy);
      const swirlZ =
        Math.sin(px * 0.004 - this.coreFlowPhase) * (40 + 60 * mid + 80 * energy);

      // Tangential flow around the y-axis
      const tanX = -dirZ * (60 + 120 * mid + 80 * energy) * (particleOnly ? 1.3 : 1);
      const tanZ = dirX * (60 + 120 * mid + 80 * energy) * (particleOnly ? 1.3 : 1);

      // Pull back toward home
      const homeStrength =
        (0.6 + bass * 1.4) * (particleOnly ? 1.7 + this.beatEnv * 1.2 : 1);
      const fx = (hx - px) * 0.01 * homeStrength;
      const fy = (hy - py) * 0.01 * homeStrength;
      const fz = (hz - pz) * 0.01 * homeStrength;

      // Vertical lift from treble
      const lift = Math.sin(t * 0.6 + i * 0.001) * (treb * 60);

      // Cymatic outward push
      const cymForce = cym * 700;

      // In particle-only mode, punch the cloud toward center on beats and
      // rebound it with cymatic waves so the mass visibly "breathes".
      const beatPinch =
        particleOnly
          ? this.beatEnv * (260 + this.bassEnv * 360) * (0.35 + p.cymaticStrength * 0.18)
          : 0;

      vx += (swirlX + tanX + fx + dirX * (cymForce - beatPinch)) * dt;
      vy += (swirlY + fy + lift) * dt;
      vz += (swirlZ + tanZ + fz + dirZ * (cymForce - beatPinch)) * dt;

      // Damping
      const damp = Math.exp(-dt * (0.6 + 0.5 * (1 - energy)) * (particleOnly ? 0.92 : 1));
      vx *= damp;
      vy *= damp;
      vz *= damp;

      // Speed cap
      const speed = Math.hypot(vx, vy, vz);
      const maxSpeed =
        RADIUS * (0.4 + flow * 0.3 + energy * 0.4) * (particleOnly ? 1.35 : 1);
      if (speed > maxSpeed) {
        const k = maxSpeed / speed;
        vx *= k;
        vy *= k;
        vz *= k;
      }

      px += vx * dt;
      py += vy * dt;
      pz += vz * dt;

      this.posArr[i3] = px;
      this.posArr[i3 + 1] = py;
      this.posArr[i3 + 2] = pz;
      this.velArr[i3] = vx;
      this.velArr[i3 + 1] = vy;
      this.velArr[i3 + 2] = vz;

      // ---- Per-particle color ----
      // Ported from the legacy VJ shader (app/templates/particles.html).
      // Uses HSV (not HSL) and lets brightness exceed 1.0 so additive
      // blending overdrives into glowing whites. Ice mode uses an explicit
      // blue-tinted RGB so the highlights have a frosty cyan core.
      const pLife = this.lifeArr[i];
      // Twinkle: cheap per-particle deterministic shimmer.
      const twinkle =
        Math.sin(t * 1.7 + i * 0.0193) * 0.5 + 0.5; // 0..1
      // Base brightness factor — VJ recipe.
      const val =
        0.55 +
        energy * 0.26 +
        this.beatEnv * 0.04 +
        twinkle * 0.16 +
        cym * 0.18;
      const sat = 0.6 + 0.38 * p.saturation;
      const intensityBoost = Math.max(0, Math.min(4, p.intensity));

      if (colorMode === 0) {
        // Glacier — explicit cool RGB tint, blue >1 so highlights pop neon.
        const v = Math.min(1.4, val * pLife);
        this.colorArr[i3] = 0.64 * v * intensityBoost;
        this.colorArr[i3 + 1] = 0.82 * v * intensityBoost;
        this.colorArr[i3 + 2] = 1.22 * v * intensityBoost;
      } else if (colorMode === 1) {
        // Rivers — hue follows radial position + twinkle shimmer.
        const h = ((baseHue + rNorm * 0.35 + twinkle * 0.025) + 1) % 1;
        const v = Math.min(1.48, val * pLife + rNorm * 0.18);
        writeHsvToRgb(this.colorArr, i3, h, sat, v, intensityBoost);
      } else if (colorMode === 2) {
        // Velocity — hue from speed + tiny per-index drift.
        const h = ((baseHue + Math.min(0.4, speed / RADIUS) + i * 8e-6) + 1) % 1;
        const v = Math.min(1.45, val * pLife);
        writeHsvToRgb(this.colorArr, i3, h, sat, v, intensityBoost);
      } else {
        // Density — radial bands oscillate hue, outer ring brightens.
        const h =
          ((baseHue +
            Math.sin(rNorm * 18 + t * 0.42) * 0.08 +
            rNorm * 0.2) + 1) % 1;
        const radialBoost = smoothstep(0.1, 0.9, rNorm) * 0.25;
        const v = Math.min(1.5, val * pLife + radialBoost);
        writeHsvToRgb(this.colorArr, i3, h, sat, v, intensityBoost);
      }
    }

    this.markCoreParticleAttributesUpdated(activeCount);
  }

  // -------------------------------------------------------------------------
  // Emergence physics — self-organizing target-shape attractor
  // -------------------------------------------------------------------------
  //
  // Each frame:
  //   1. For every particle, sample a "target" position from the chosen
  //      shape (sphere, torus, cube, ribbon, blob).
  //   2. Compute a soft attraction toward that target (cohesion).
  //   3. Add Perlin-style noise so the cloud breathes/wobbles instead of
  //      sitting perfectly still.
  //   4. Bass envelope adds outward repulsion that fights the cohesion —
  //      kicks momentarily blow the cloud apart, then it collapses back.
  //   5. Per-particle velocity integrates with damping; speed cap.
  //
  // Reads as a lava-lamp / blob simulation that morphs with the music —
  // very different feel from the orbital physics in updateParticles().

  /**
   * Per-particle "shape index" — assigned once at scene init from the hash
   * function, used by sampleEmergenceTarget so each particle has a stable
   * "spot" inside the chosen shape (avoids them swapping homes every
   * frame, which would look like noise).
   */
  private emergenceShapeIdx = new Float32Array(0);
  private readonly emergenceTarget = { x: 0, y: 0, z: 0 };

  private updateEmergence(dt: number, energy: number, fx: ConceptEffects): void {
    const p = this.params;
    const t = this.elapsed;

    // Lazy-init the per-particle shape index buffer the first time
    // emergence runs. Cheap; only happens once.
    const activeCount = this.ensureCoreParticleCapacity(p.qualityTier);
    if (this.emergenceShapeIdx.length !== this.particleCapacity) {
      this.emergenceShapeIdx = new Float32Array(this.particleCapacity);
      for (let i = 0; i < this.particleCapacity; i++) {
        this.emergenceShapeIdx[i] = hash(i * 0.31 + 7.7);
      }
    }

    this.particleGeo.setDrawRange(0, activeCount);

    const positions = this.posArr;
    const velocities = this.velArr;
    const colors = this.colorArr;

    // ---- Tunable physics constants (driven partly by params) ----
    // Cohesion gain: how strongly each particle is pulled toward its
    // target. Higher = snaps into shape faster.
    const cohereGain = 2.4 + this.midEnv * 1.2;
    // Bass repulsion: kicks blow the cloud outward.
    const bassPush = this.bassEnv * p.bassGravity * 0.95;
    // Noise field strength.
    const noiseAmp = (0.6 + p.midCurl * 0.4) * (1 + this.trebEnv * 0.3);
    // Per-frame damping (closer to 1 = floatier).
    const damp = Math.exp(-dt * (1.4 - this.bassEnv * 0.6));
    // Speed cap.
    const maxSpeed = RADIUS * (0.6 + energy * 0.4);
    // Outer boundary so escapees snap back instead of vanishing.
    const boundR = RADIUS * 1.6;

    // Cymatic strength still creates outward pulses; reuse the same ring
    // mechanism so the existing beat overlay stays in sync.
    const ringForce = (rNorm: number): number => {
      let total = 0;
      for (const ring of this.rings) {
        const speed = 1.05;
        const width = 24;
        const wave = Math.exp(-Math.abs(rNorm - ring.age * speed) * width);
        total += wave * ring.power;
      }
      return total * p.cymaticStrength;
    };

    const colorMode = p.colorMode;
    const baseHue = ((fx.hueDeg / 360) % 1 + 1) % 1;
    const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));

    // ---- Per-particle integration loop ----
    for (let i = 0; i < activeCount; i++) {
      const i3 = i * 3;
      const px = positions[i3];
      const py = positions[i3 + 1];
      const pz = positions[i3 + 2];
      let vx = velocities[i3];
      let vy = velocities[i3 + 1];
      let vz = velocities[i3 + 2];

      // 1. Target-shape position for this particle.
      const target = this.emergenceTarget;
      sampleEmergenceTarget(
        target,
        this.emergenceShapeIdx[i],
        i,
        p.emergenceShape,
        t,
      );

      // 2. Cohesion: spring-like pull toward the target.
      const dx = target.x - px;
      const dy = target.y - py;
      const dz = target.z - pz;
      vx += dx * cohereGain * dt;
      vy += dy * cohereGain * dt;
      vz += dz * cohereGain * dt;

      // 3. Noise (deterministic per-particle for stability).
      const nseed = i * 0.013 + t * 0.4;
      const nx = Math.sin(nseed) * noiseAmp * RADIUS * 0.6;
      const ny = Math.cos(nseed * 1.31 + 1.7) * noiseAmp * RADIUS * 0.6;
      const nz = Math.sin(nseed * 0.87 + 3.4) * noiseAmp * RADIUS * 0.6;
      vx += nx * dt;
      vy += ny * dt;
      vz += nz * dt;

      // 4. Bass repulsion (radial outward push from origin).
      const distFromOrigin = Math.max(1, Math.sqrt(px * px + py * py + pz * pz));
      const dirX = px / distFromOrigin;
      const dirY = py / distFromOrigin;
      const dirZ = pz / distFromOrigin;
      const push = bassPush * RADIUS * 1.4;
      vx += dirX * push * dt;
      vy += dirY * push * dt;
      vz += dirZ * push * dt;

      // Cymatic shockwave additive force (uses same ring buffer as orbital).
      const rNorm = distFromOrigin / RADIUS;
      const cym = ringForce(rNorm);
      const cymForce = cym * 800;
      vx += dirX * cymForce * dt;
      vy += dirY * cymForce * dt;
      vz += dirZ * cymForce * dt;

      // 5. Boundary spring (only kicks in past boundR).
      if (distFromOrigin > boundR) {
        const k = ((distFromOrigin - boundR) / boundR) * 8 * Math.min(3, dt * 60);
        vx -= dirX * k;
        vy -= dirY * k;
        vz -= dirZ * k;
      }

      // 6. Damping.
      vx *= damp;
      vy *= damp;
      vz *= damp;

      // 7. Speed cap.
      const sp = Math.sqrt(vx * vx + vy * vy + vz * vz);
      if (sp > maxSpeed) {
        const k = maxSpeed / sp;
        vx *= k; vy *= k; vz *= k;
      }

      // 8. Integrate.
      const npx = px + vx * dt;
      const npy = py + vy * dt;
      const npz = pz + vz * dt;
      positions[i3] = npx;
      positions[i3 + 1] = npy;
      positions[i3 + 2] = npz;
      velocities[i3] = vx;
      velocities[i3 + 1] = vy;
      velocities[i3 + 2] = vz;

      // 9. Color (same recipe as the orbital path so cross-fades match).
      let h = baseHue;
      let s = 0.85 * p.saturation + 0.05;
      let l = 0.7 + energy * 0.22 + this.beatEnv * 0.08;
      if (colorMode === 0) {
        h = 0.55;
        s = 0.4 * p.saturation;
      } else if (colorMode === 1) {
        h = (baseHue + rNorm * 0.35) % 1;
      } else if (colorMode === 2) {
        h = (baseHue + Math.min(0.4, sp / RADIUS)) % 1;
      } else if (colorMode === 3) {
        h = (baseHue + Math.sin(rNorm * 18 + t * 0.4) * 0.08 + rNorm * 0.2) % 1;
      }
      l = Math.min(1, l + cym * 0.4);
      writeHsvToRgb(
        colors,
        i3,
        ((h % 1) + 1) % 1,
        s,
        l,
        intensityBoost,
      );
    }

    this.markCoreParticleAttributesUpdated(activeCount);
  }

  // -------------------------------------------------------------------------
  // Strings
  // -------------------------------------------------------------------------

  private updateStrings(dt: number, energy: number, fx: ConceptEffects): void {
    const positions = this.stringPos;
    const p = this.params;
    this.stringsPhase += dt * (0.3 + p.flowSpeed * 0.2);
    this.stringsDriftPhase += dt * 0.3;
    let seg = 0;
    for (let s = 0; s < STRING_STRANDS; s++) {
      const aSeed = hash(s * 12.9898);
      const baseAngle = aSeed * Math.PI * 2;
      let prevX = 0;
      let prevY = 0;
      let prevZ = 0;
      for (let j = 0; j < STRING_STEPS; j++) {
        const f = j / (STRING_STEPS - 1);
        const radius = RADIUS * (0.1 + f * 0.85);
        const angle =
          baseAngle +
          f * 4.5 +
          Math.sin(this.stringsPhase + s * 0.5 + f * 8) *
            (0.2 + this.midEnv * 0.4);
        const y =
          (hash(s * 78.233 + j * 17) - 0.5) * RADIUS * 0.18 +
          Math.sin(f * Math.PI + this.stringsDriftPhase) * RADIUS * 0.08;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        if (j > 0) {
          const o = seg * 6;
          positions[o] = prevX;
          positions[o + 1] = prevY;
          positions[o + 2] = prevZ;
          positions[o + 3] = x;
          positions[o + 4] = y;
          positions[o + 5] = z;
          seg++;
        }
        prevX = x;
        prevY = y;
        prevZ = z;
      }
    }
    (this.strings.geometry.attributes.position as THREE.BufferAttribute).needsUpdate =
      true;
    const mat = this.strings.material as THREE.LineBasicMaterial;
    mat.opacity = Math.min(0.7, 0.18 + energy * 0.4 + this.beatEnv * 0.15);
    mat.color.copy(paletteColor(fx.hueDeg + 30, 0.6));
  }

  // -------------------------------------------------------------------------
  // Lattice
  // -------------------------------------------------------------------------

  private updateLattice(energy: number, fx: ConceptEffects): void {
    const positions = this.latticePos;
    const t = this.elapsed;
    const points: THREE.Vector3[][] = [];
    for (let r = 0; r < LATTICE_RINGS; r++) {
      points[r] = [];
      const rf = r / (LATTICE_RINGS - 1);
      const radius = RADIUS * (0.15 + rf * 0.95);
      for (let s = 0; s < LATTICE_SPOKES; s++) {
        const a = (s / LATTICE_SPOKES) * Math.PI * 2 + t * 0.04 * (1 + rf);
        const y =
          Math.sin(a * 2 + t * 0.3 + r) * RADIUS * 0.05 +
          (rf - 0.5) * RADIUS * 0.18;
        points[r][s] = new THREE.Vector3(
          Math.cos(a) * radius,
          y,
          Math.sin(a) * radius,
        );
      }
    }
    let seg = 0;
    // Ring segments
    for (let r = 0; r < LATTICE_RINGS; r++) {
      for (let s = 0; s < LATTICE_SPOKES; s++) {
        const a = points[r][s];
        const b = points[r][(s + 1) % LATTICE_SPOKES];
        const o = seg * 6;
        positions[o] = a.x;
        positions[o + 1] = a.y;
        positions[o + 2] = a.z;
        positions[o + 3] = b.x;
        positions[o + 4] = b.y;
        positions[o + 5] = b.z;
        seg++;
      }
    }
    // Spoke segments
    for (let r = 0; r < LATTICE_RINGS - 1; r++) {
      for (let s = 0; s < LATTICE_SPOKES; s++) {
        const a = points[r][s];
        const b = points[r + 1][s];
        const o = seg * 6;
        positions[o] = a.x;
        positions[o + 1] = a.y;
        positions[o + 2] = a.z;
        positions[o + 3] = b.x;
        positions[o + 4] = b.y;
        positions[o + 5] = b.z;
        seg++;
      }
    }
    (this.lattice.geometry.attributes.position as THREE.BufferAttribute).needsUpdate =
      true;
    const mat = this.lattice.material as THREE.LineBasicMaterial;
    mat.opacity = Math.min(0.5, 0.12 + energy * 0.3 + this.beatEnv * 0.1);
    mat.color.copy(paletteColor(fx.hueDeg + 80, 0.55));
  }

  // -------------------------------------------------------------------------
  // Aurora ribbon
  // -------------------------------------------------------------------------

  private updateAurora(energy: number, fx: ConceptEffects): void {
    const positions = this.auroraPos;
    const t = this.elapsed;
    let seg = 0;
    const strips = 6;
    const steps = 30;
    const width = RADIUS * 1.6;
    const height = RADIUS * 0.4;
    const speed = t * 0.4;
    for (let s = 0; s < strips; s++) {
      let prevX = 0;
      let prevY = 0;
      let prevZ = 0;
      const baseY = (s / (strips - 1) - 0.5) * height * 1.2;
      for (let j = 0; j < steps; j++) {
        const f = j / (steps - 1);
        const phase = s * 0.74 + f * 8 + speed;
        const wave =
          Math.sin(phase) * RADIUS * 0.06 +
          Math.sin(phase * 1.9 + t * 0.2) * RADIUS * 0.025;
        const x = (f - 0.5) * width;
        const y =
          baseY +
          Math.sin(phase * 0.7) * RADIUS * 0.06 +
          this.trebEnv * RADIUS * 0.06 * Math.sin(phase * 2);
        const z = wave + this.midEnv * RADIUS * 0.04;
        if (j > 0 && seg < BioclastScene.AURORA_SEGMENTS) {
          const o = seg * 6;
          positions[o] = prevX;
          positions[o + 1] = prevY;
          positions[o + 2] = prevZ;
          positions[o + 3] = x;
          positions[o + 4] = y;
          positions[o + 5] = z;
          seg++;
        }
        prevX = x;
        prevY = y;
        prevZ = z;
      }
    }
    while (seg < BioclastScene.AURORA_SEGMENTS) {
      const o = seg * 6;
      positions[o] = 0;
      positions[o + 1] = 0;
      positions[o + 2] = 0;
      positions[o + 3] = 0;
      positions[o + 4] = 0;
      positions[o + 5] = 0;
      seg++;
    }
    (this.aurora.geometry.attributes.position as THREE.BufferAttribute).needsUpdate =
      true;
    const mat = this.aurora.material as THREE.LineBasicMaterial;
    mat.opacity = Math.min(0.6, 0.15 + energy * 0.35 + this.trebEnv * 0.15);
    mat.color.copy(paletteColor(fx.hueDeg - 40, 0.65));
  }

  // -------------------------------------------------------------------------
  // Warp tunnel
  // -------------------------------------------------------------------------

  private updateWarp(dt: number, energy: number, fx: ConceptEffects): void {
    // Geometry is in CAMERA-LOCAL space because `this.warp` is parented to
    // the camera. Forward in camera space is -Z, so we extend the tunnel
    // along -Z and let the camera's world transform place + orient it.
    const positions = this.warpPos;
    const p = this.params;
    let seg = 0;
    this.warpTravel += dt *
      (2 + p.flowSpeed * fx.flowMul * 0.8 + energy * 1.2 + this.beatEnv * 0.5);
    const travel = this.warpTravel;
    // Tunnel depth stretches from "just past the lens" out to several radii.
    const depth = RADIUS * 3.4;
    // Wide spread so near-field stars stream past the edges of the FOV
    // instead of crowding the center pinhole.
    const spread = RADIUS * 1.2;
    // Distance from camera at which tail particles spawn. Anything closer
    // than this would clip through the near plane on a pinch-zoom.
    // cameraZoom > 1 pulls the tunnel closer; < 1 pushes it back, so the
    // pinch gesture changes how "deep" you feel inside the tunnel.
    const zoom = Math.max(0.15, Math.min(3.5, p.cameraZoom));
    const nearStart = (-RADIUS * 0.05) / zoom;
    for (let i = 0; i < BioclastScene.WARP_SEGMENTS; i++) {
      const h0 = hash(i * 12.9898);
      const h1 = hash(i * 78.233 + 17.17);
      const h2 = hash(i * 37.719 + 91.7);
      const h3 = hash(i * 11.135 + 41.3);
      const angle = h0 * Math.PI * 2;
      const lane = Math.pow(h1, 0.36);
      // 0..1 phase: 0 = right at the camera, 1 = far in the distance.
      const phase = (h3 + travel * (0.12 + h2 * 0.05)) % 1;
      const near = Math.max(0.02, phase);
      // Perspective-style projection: far stars sit close to the central
      // axis, near stars fan out — sells the speed.
      const project = 0.18 + near * 2.25;
      const rad = spread * (0.05 + lane * 1.7) * project;
      const x = Math.cos(angle) * rad;
      const y = Math.sin(angle) * rad * 0.56;
      // -Z = forward in camera space. Build the tunnel diving away from
      // the lens and let it stream toward the viewer as `travel` advances.
      const z = nearStart - near * depth;
      const tailProject =
        project + 0.04 * (0.75 + near * 3.1 + h2 * 0.65);
      const tailRad = spread * (0.05 + lane * 1.7) * tailProject;
      const tx = Math.cos(angle) * tailRad;
      const ty = Math.sin(angle) * tailRad * 0.56;
      // Tail trails behind the head (further from lens) so motion blur
      // streaks read as forward velocity.
      const tz = z - depth * (0.04 + 0.04 * (0.55 + near * 1.8));
      const o = seg * 6;
      positions[o] = x;
      positions[o + 1] = y;
      positions[o + 2] = z;
      positions[o + 3] = tx;
      positions[o + 4] = ty;
      positions[o + 5] = tz;
      seg++;
    }
    (this.warp.geometry.attributes.position as THREE.BufferAttribute).needsUpdate =
      true;
    const mat = this.warp.material as THREE.LineBasicMaterial;
    mat.opacity = Math.min(0.7, 0.18 + energy * 0.3 + this.beatEnv * 0.2);
    mat.color.copy(paletteColor(fx.hueDeg + 10, 0.7));
  }

  // -------------------------------------------------------------------------
  // Cathedral pillars
  // -------------------------------------------------------------------------

  private updateCathedral(energy: number, fx: ConceptEffects): void {
    // World-space cathedral: the nave runs along the Z axis, centered at
    // the origin. Pillars stand vertically (Y axis) on each side. Lives
    // in the world (not parented to the camera) so the user can orbit
    // around it, zoom in/out, and see the architecture from any angle.
    const positions = this.cathedralPos;
    const t = this.elapsed;
    const p = this.params;
    const bays = BioclastScene.CATHEDRAL_BAYS;
    const cols = BioclastScene.CATHEDRAL_COLUMNS;
    // Centered nave: bays span [-naveHalf .. +naveHalf] along Z.
    const naveHalf = RADIUS * 1.2;
    const height = RADIUS * 0.55;
    const width = RADIUS * 0.6;
    let seg = 0;

    for (let b = 0; b < bays; b++) {
      const f = b / (bays - 1);
      // Bay z-position lerps across the nave with a tiny breathing wobble.
      const z = (f - 0.5) * 2 * naveHalf +
        Math.sin(t * 0.18 + f * 5) * RADIUS * 0.03;
      // Vertical pillars across the bay's width.
      for (let c = 0; c < cols; c++) {
        const side = c / (cols - 1);
        const x = (side - 0.5) * width;
        const y0 = -height * 0.5;
        const y1 =
          height * (Math.sin(side * Math.PI) * 0.7 + 0.3) *
          (1 + this.trebEnv * 0.18);
        if (seg < BioclastScene.CATHEDRAL_SEGMENTS) {
          const o = seg * 6;
          positions[o] = x;
          positions[o + 1] = y0;
          positions[o + 2] = z;
          positions[o + 3] = x;
          positions[o + 4] = y1;
          positions[o + 5] = z;
          seg++;
        }
      }
      // Arch crown segments connecting adjacent pillars (the curve at
      // the top of each bay). Reads as the ribbed cathedral ceiling.
      for (let c = 0; c < cols - 1; c++) {
        const sideA = c / (cols - 1);
        const sideB = (c + 1) / (cols - 1);
        const xA = (sideA - 0.5) * width;
        const xB = (sideB - 0.5) * width;
        const yA = height * (Math.sin(sideA * Math.PI) * 0.7 + 0.3);
        const yB = height * (Math.sin(sideB * Math.PI) * 0.7 + 0.3);
        const zA = z + Math.sin(t + sideA * 4 + b) * RADIUS * 0.012;
        if (seg < BioclastScene.CATHEDRAL_SEGMENTS) {
          const o = seg * 6;
          positions[o] = xA;
          positions[o + 1] = yA;
          positions[o + 2] = zA;
          positions[o + 3] = xB;
          positions[o + 4] = yB;
          positions[o + 5] = zA;
          seg++;
        }
      }
    }
    while (seg < BioclastScene.CATHEDRAL_SEGMENTS) {
      const o = seg * 6;
      positions[o] = 0;
      positions[o + 1] = 0;
      positions[o + 2] = 0;
      positions[o + 3] = 0;
      positions[o + 4] = 0;
      positions[o + 5] = 0;
      seg++;
    }
    (this.cathedral.geometry.attributes.position as THREE.BufferAttribute).needsUpdate =
      true;
    const mat = this.cathedral.material as THREE.LineBasicMaterial;
    mat.opacity = Math.min(0.85, 0.35 + energy * 0.3 + this.beatEnv * 0.2);
    mat.color.copy(paletteColor(fx.hueDeg + 5, 0.65));
    void p; // p is unused after removing cathedralReverse
  }

  // -------------------------------------------------------------------------
  // DNA Helix — two camera-locked particle strands + beat-flashing rungs
  // -------------------------------------------------------------------------

  private updateDnaHelix(dt: number, energy: number, fx: ConceptEffects): void {
    // Vertical helix in WORLD SPACE. The axis runs along Y (up) and the
    // particles wind around it like a real DNA molecule. Lives in world
    // space so the orbit camera can frame it from the side — that's the
    // only angle from which a helix actually *reads* as a helix.
    // (Looking down the helix axis collapses all the depth and turns the
    // rungs into a meaningless starburst pattern.)
    const p = this.params;
    const turns = BioclastScene.DNA_TURNS;
    const perTurn = BioclastScene.DNA_PER_TURN;
    const totalSteps = turns * perTurn;
    // Helix runs from -helixHalfHeight to +helixHalfHeight along Y.
    const helixHalfHeight = RADIUS * 0.85;
    const helixRadius = RADIUS * 0.32 * (0.9 + p.bassGravity * 0.05);
    // Spin rate locks to bass envelope so kicks visibly rotate the helix
    // around its vertical axis.
    const spinRate = 0.4 + this.bassEnv * 1.6 + energy * 0.6;
    this.dnaPhase += dt * spinRate;
    const phase0 = this.dnaPhase;

    const dnaPos = this.dnaPos;
    const dnaCol = this.dnaCol;
    let writeIdx = 0;

    // Two strands offset by π so they form an interlocking double helix.
    for (let strand = 0; strand < BioclastScene.DNA_STRANDS; strand++) {
      const strandPhase = strand * Math.PI;
      const hueOffset = strand === 0 ? 0 : 35;
      for (let s = 0; s < totalSteps; s++) {
        const f = s / (totalSteps - 1);
        // Y position lerps along the vertical axis.
        const y = (f - 0.5) * 2 * helixHalfHeight;
        // Helical angle in the XZ plane.
        const theta = phase0 + strandPhase + f * turns * Math.PI * 2;
        const x = Math.cos(theta) * helixRadius;
        const z = Math.sin(theta) * helixRadius;
        const o3 = writeIdx * 3;
        dnaPos[o3] = x;
        dnaPos[o3 + 1] = y;
        dnaPos[o3 + 2] = z;
        // Color: per-strand hue + treble shimmer + position-down-helix.
        const hue = ((fx.hueDeg + hueOffset + f * 60) / 360) % 1;
        const v = Math.min(
          1.6,
          0.9 + this.trebEnv * 0.4 + this.beatEnv * 0.35 + (1 - Math.abs(f - 0.5) * 2) * 0.2,
        );
        const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
        writeHsvToRgb(
          dnaCol,
          o3,
          hue < 0 ? hue + 1 : hue,
          0.85,
          v,
          intensityBoost,
        );
        writeIdx++;
      }
    }
    (this.dnaPoints.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.dnaPoints.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    const dnaMat = this.dnaPoints.material as THREE.PointsMaterial;
    dnaMat.size = Math.max(7, 32 * p.starSize) * (1 + this.beatEnv * 0.5);
    dnaMat.opacity = Math.min(1, 0.9 + this.beatEnv * 0.1) * Math.max(0.4, p.opacity);

    // Cross-rungs: line segments connecting the two strands, brightness
    // pulsing on every beat so kicks visibly "rung" the helix.
    const rungPos = this.dnaRungsPos;
    const rungsPerTurn = BioclastScene.DNA_RUNGS_PER_TURN;
    let rungIdx = 0;
    for (let turn = 0; turn < turns; turn++) {
      for (let k = 0; k < rungsPerTurn; k++) {
        const f = (turn + k / rungsPerTurn) / turns;
        const y = (f - 0.5) * 2 * helixHalfHeight;
        const theta = phase0 + f * turns * Math.PI * 2;
        const x0 = Math.cos(theta) * helixRadius;
        const z0 = Math.sin(theta) * helixRadius;
        const x1 = Math.cos(theta + Math.PI) * helixRadius;
        const z1 = Math.sin(theta + Math.PI) * helixRadius;
        const o = rungIdx * 6;
        rungPos[o] = x0;
        rungPos[o + 1] = y;
        rungPos[o + 2] = z0;
        rungPos[o + 3] = x1;
        rungPos[o + 4] = y;
        rungPos[o + 5] = z1;
        rungIdx++;
      }
    }
    (this.dnaRungs.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    const rungMat = this.dnaRungs.material as THREE.LineBasicMaterial;
    rungMat.opacity = Math.min(0.9, 0.18 + energy * 0.25 + this.beatEnv * 0.55);
    rungMat.color.copy(paletteColor(fx.hueDeg + 22, 0.85));
  }

  // -------------------------------------------------------------------------
  // Pulse Vortex — inward-spiraling disc + beat shockwave rings
  // -------------------------------------------------------------------------

  private updateVortex(dt: number, energy: number, fx: ConceptEffects): void {
    const positions = this.vortexPos;
    const colors = this.vortexCol;
    const seeds = this.vortexSeed;
    const t = this.elapsed;
    const p = this.params;
    const activeCount = fixedLayerCount(BioclastScene.VORTEX_COUNT, p.qualityTier);
    this.vortex.geometry.setDrawRange(0, activeCount);

    // Particles drift inward (theta accelerates toward small radii).
    // On beat hits, the vortex briefly pushes outward — the bass envelope
    // injects a radial impulse that the user reads as "the music breathes
    // the vortex out and pulls it back in."
    // Note: breath amplitude is GENTLE (0.12 max) so particles never get
    // blown beyond the visible field — the visual stays as a dense disc.
    const breath = (this.bassEnv - 0.3) * 0.12 * p.bassGravity;
    const swirlRate = 0.6 + energy * 0.8 + this.bassEnv * 0.6;
    this.vortexPhase += dt * swirlRate;

    for (let i = 0; i < activeCount; i++) {
      const i3 = i * 3;
      const r0 = seeds[i3];
      const theta0 = seeds[i3 + 1];
      const wobble = seeds[i3 + 2];
      // Inward spiral: angular speed grows as radius shrinks (Kepler-ish).
      // Theta accumulates with time + per-particle phase; radius oscillates
      // around r0 with the bass breath.
      const r = Math.max(0.04, r0 + breath * (0.4 + wobble * 0.6));
      const angVel = swirlRate * (0.6 + 0.4 / Math.max(0.2, r));
      const theta = theta0 + this.vortexPhase * (angVel / swirlRate);
      const x = Math.cos(theta) * r * RADIUS * 1.1;
      const z = Math.sin(theta) * r * RADIUS * 1.1;
      // Vertical: thin disc with a slight wobble for organic feel.
      const y =
        Math.sin(t * 0.5 + theta0 * 3) * RADIUS * 0.04 * wobble +
        (wobble - 0.5) * RADIUS * 0.02;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;

      // Color: inner = hot core (high V), outer = cooler hue rim.
      const innerness = 1 - r; // 0..1, 1 = at center
      const hue = ((fx.hueDeg + (1 - innerness) * 40) / 360) % 1;
      const v = Math.min(
        1.7,
        0.6 + innerness * 0.7 + this.beatEnv * 0.35 + this.trebEnv * 0.18,
      );
      const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
      writeHsvToRgb(
        colors,
        i3,
        hue < 0 ? hue + 1 : hue,
        0.85,
        v,
        intensityBoost,
      );
    }
    this.markGeometryAttributesUpdated(
      this.vortex.geometry,
      activeCount,
      ["position", "color"],
    );
    const vortexMat = this.vortex.material as THREE.PointsMaterial;
    vortexMat.size = Math.max(9, 42 * p.starSize) * (1 + this.beatEnv * 0.6);
    vortexMat.opacity = Math.min(1, 0.85 + this.beatEnv * 0.15) * Math.max(0.4, p.opacity);

    // Spawn a fresh shockwave ring when a strong beat lands.
    if (this.beatEdge) {
      // Reuse the cymatic-ring power scaled to the beat envelope.
      const power = Math.min(1.6, this.features.beat * 1.2 + this.bassEnv * 0.5);
      this.spawnVortexRing(power);
    }
    this.updateVortexRings(dt, fx);
  }

  private spawnVortexRing(power: number): void {
    this.vortexRingSpawns += 1;
    let slot = this.vortexRingState.findIndex((s) => !s.live);
    if (slot === -1) {
      // Recycle the oldest ring.
      slot = 0;
      let oldest = -1;
      for (let i = 0; i < this.vortexRingState.length; i++) {
        if (this.vortexRingState[i].age > oldest) {
          oldest = this.vortexRingState[i].age;
          slot = i;
        }
      }
    }
    this.vortexRingState[slot] = { age: 0, power, live: true };
  }

  /**
   * Force-hide every vortex ring + zero its state. Called from the main
   * render loop whenever `layerVortex` is false, since the ring meshes
   * live in world space (not in the vortex Points group) and would
   * otherwise stay visible at their last-set opacity until the layer
   * was toggled back on.
   */
  private hideVortexRings(): void {
    for (let i = 0; i < this.vortexRings.length; i++) {
      const mesh = this.vortexRings[i];
      (mesh.material as THREE.MeshBasicMaterial).opacity = 0;
      mesh.visible = false;
      this.vortexRingState[i] = { age: 99, power: 0, live: false };
    }
  }

  private updateVortexRings(dt: number, fx: ConceptEffects): void {
    for (let i = 0; i < this.vortexRings.length; i++) {
      const mesh = this.vortexRings[i];
      const state = this.vortexRingState[i];
      if (!state.live) {
        (mesh.material as THREE.MeshBasicMaterial).opacity = 0;
        mesh.visible = false;
        continue;
      }
      state.age += dt;
      state.power *= Math.exp(-1.5 * dt);
      if (state.power < 0.02) {
        state.live = false;
        mesh.visible = false;
        continue;
      }
      mesh.visible = true;
      const r = Math.min(RADIUS * 1.8, state.age * 950);
      mesh.scale.setScalar(Math.max(1, r));
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, state.power * 0.6) * Math.exp(-state.age * 1.2);
      mat.color.copy(paletteColor(fx.hueDeg + i * 18, 0.85));
    }
  }

  // -------------------------------------------------------------------------
  // Audio Tide — live FFT terrain wave (frequency spectrum as 3D landscape)
  // -------------------------------------------------------------------------

  /**
   * Frame counter for the Audio Tide scroll. The grid only shifts back
   * one row every N frames so the wave moves at a leisurely pace the
   * user can actually fly through.
   */
  private tideScrollElapsed = 0;
  private static readonly TIDE_SCROLL_INTERVAL_SECONDS = 3 / 60;

  private updateTide(dt: number, energy: number, fx: ConceptEffects): void {
    const W = BioclastScene.TIDE_W;
    const H = BioclastScene.TIDE_H;
    const positions = this.tidePos;
    const colors = this.tideCol;
    const history = this.tideHistory;
    const f = this.features;
    const spectrum = f.spectrum; // Float32Array, AudioFeatures bands

    // Each Nth frame: shift history one row toward the back, write a new
    // row at the front (row 0) sampled from the live spectrum. Result is
    // a slow rolling waterfall where time flows from the camera into the
    // distance — slow enough that the user can fly through it without
    // the terrain blurring past.
    this.tideScrollElapsed += dt;
    const shouldScroll =
      this.tideScrollElapsed >= BioclastScene.TIDE_SCROLL_INTERVAL_SECONDS;
    if (shouldScroll) {
      this.tideScrollElapsed %= BioclastScene.TIDE_SCROLL_INTERVAL_SECONDS;
      for (let row = H - 1; row > 0; row--) {
        for (let col = 0; col < W; col++) {
          history[row * W + col] = history[(row - 1) * W + col];
        }
      }
    }
    if (spectrum && spectrum.length > 0) {
      // Resample the spectrum to the grid width. Sqrt curve emphasizes
      // mids/highs visually since raw magnitudes drop off fast.
      for (let col = 0; col < W; col++) {
        const sIdx = Math.floor((col / W) * spectrum.length);
        const mag = Math.min(1, Math.sqrt(spectrum[sIdx] ?? 0));
        history[col] = mag;
      }
    } else {
      // Fall back to band envelopes spread across the grid if spectrum
      // isn't available (mic providers may not emit a per-bin array).
      for (let col = 0; col < W; col++) {
        const f01 = col / (W - 1);
        const m =
          f01 < 0.33 ? this.bassEnv :
          f01 < 0.66 ? this.midEnv :
          this.trebEnv;
        history[col] = Math.min(1, m * (0.7 + Math.sin(col * 0.3 + this.elapsed * 2) * 0.3));
      }
    }

    // Build the wireframe each frame from history. Geometry is in world
    // space; the scene preset positions the camera above and tilts down.
    // The grid spans wide and deep so the user has plenty of "terrain"
    // to fly through without seeing the edges.
    const cellW = (RADIUS * 5.5) / (W - 1);
    const cellD = (RADIUS * 5.0) / (H - 1);
    const baseY = -RADIUS * 0.25;
    const heightScale = RADIUS * 0.85;
    let seg = 0;

    // Horizontal ridge lines (one per row, spans the width).
    for (let row = 0; row < H; row++) {
      for (let col = 0; col < W - 1; col++) {
        const x0 = (col - (W - 1) / 2) * cellW;
        const x1 = (col + 1 - (W - 1) / 2) * cellW;
        const z = -row * cellD; // back rows are further away
        const y0 = baseY + history[row * W + col] * heightScale;
        const y1 = baseY + history[row * W + col + 1] * heightScale;
        const o = seg * 6;
        positions[o] = x0;
        positions[o + 1] = y0;
        positions[o + 2] = z;
        positions[o + 3] = x1;
        positions[o + 4] = y1;
        positions[o + 5] = z;
        // Color hue from frequency (column position), brightness from height.
        const hue = ((fx.hueDeg + (col / W) * 200) / 360) % 1;
        const v0 = Math.min(1.5, 0.4 + history[row * W + col] * 1.1);
        const v1 = Math.min(1.5, 0.4 + history[row * W + col + 1] * 1.1);
        const intensityBoost = Math.max(0.5, Math.min(3, this.params.intensity));
        writeHsvToRgb(colors, o, hue < 0 ? hue + 1 : hue, 0.8, v0, intensityBoost);
        writeHsvToRgb(colors, o + 3, hue < 0 ? hue + 1 : hue, 0.8, v1, intensityBoost);
        seg++;
      }
    }
    // Vertical struts every 4th column to sketch in depth without choking
    // the GPU on a fully-dense wireframe.
    for (let col = 0; col < W; col += 4) {
      for (let row = 0; row < H - 1; row++) {
        if (seg >= BioclastScene.TIDE_SEGMENTS) break;
        const x = (col - (W - 1) / 2) * cellW;
        const z0 = -row * cellD;
        const z1 = -(row + 1) * cellD;
        const y0 = baseY + history[row * W + col] * heightScale;
        const y1 = baseY + history[(row + 1) * W + col] * heightScale;
        const o = seg * 6;
        positions[o] = x;
        positions[o + 1] = y0;
        positions[o + 2] = z0;
        positions[o + 3] = x;
        positions[o + 4] = y1;
        positions[o + 5] = z1;
        const hue = ((fx.hueDeg + (col / W) * 200) / 360) % 1;
        const v = Math.min(1.4, 0.35 + (history[row * W + col] + history[(row + 1) * W + col]) * 0.55);
        const intensityBoost = Math.max(0.5, Math.min(3, this.params.intensity));
        writeHsvToRgb(colors, o, hue < 0 ? hue + 1 : hue, 0.7, v, intensityBoost);
        colors[o + 3] = colors[o];
        colors[o + 4] = colors[o + 1];
        colors[o + 5] = colors[o + 2];
        seg++;
      }
    }
    // Zero-fill the rest of the buffer if we under-wrote.
    while (seg < BioclastScene.TIDE_SEGMENTS) {
      const o = seg * 6;
      positions[o] = 0; positions[o + 1] = 0; positions[o + 2] = 0;
      positions[o + 3] = 0; positions[o + 4] = 0; positions[o + 5] = 0;
      seg++;
    }
    (this.tide.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.tide.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    const tideMat = this.tide.material as THREE.LineBasicMaterial;
    tideMat.opacity = Math.min(0.9, 0.55 + energy * 0.35 + this.beatEnv * 0.15);
  }

  // -------------------------------------------------------------------------
  // Orbital Rings — three concentric particle bands tied to freq bands
  // -------------------------------------------------------------------------

  private updateOrbital(dt: number, energy: number, fx: ConceptEffects): void {
    const positions = this.orbitalPos;
    const colors = this.orbitalCol;
    const seeds = this.orbitalSeed;
    const t = this.elapsed;
    const p = this.params;
    const perRing = BioclastScene.ORBITAL_PER_RING;

    // Three rings: bass (innermost, slow), mid (middle, medium), treble (outer, fast).
    // Each ring's rotation rate is tied to its frequency band envelope.
    const rates = [
      0.12 + this.bassEnv * 0.6,   // ring 0: bass
      0.2 + this.midEnv * 0.9,     // ring 1: mid
      0.35 + this.trebEnv * 1.4,   // ring 2: treble
    ];
    for (let ring = 0; ring < rates.length; ring++) {
      this.orbitalPhases[ring] += dt * rates[ring];
    }
    // Ring radii scale with the field — outer ring sits ~1.0 RADIUS,
    // middle 0.65, inner 0.4. Each one gets a subtle tilt that precesses.
    const radii = [RADIUS * 0.4, RADIUS * 0.65, RADIUS * 1.0];
    const tilts = [
      Math.sin(t * 0.18) * 0.25,
      Math.sin(t * 0.13 + 2.1) * 0.32,
      Math.sin(t * 0.09 + 4.2) * 0.4,
    ];

    for (let r = 0; r < BioclastScene.ORBITAL_RING_COUNT; r++) {
      const radius = radii[r] * (1 + this.beatEnv * 0.06);
      const tilt = tilts[r];
      const sinTilt = Math.sin(tilt);
      const cosTilt = Math.cos(tilt);
      for (let s = 0; s < perRing; s++) {
        const idx = r * perRing + s;
        const i2 = idx * 2;
        const theta0 = seeds[i2];
        const yJitter = seeds[i2 + 1];
        const theta = theta0 + this.orbitalPhases[r];
        const x0 = Math.cos(theta) * radius;
        const z0 = Math.sin(theta) * radius;
        // Apply ring tilt around X axis: rotates (y,z) about origin.
        const y = z0 * sinTilt + yJitter * RADIUS * 0.04;
        const z = z0 * cosTilt;
        const o3 = idx * 3;
        positions[o3] = x0;
        positions[o3 + 1] = y;
        positions[o3 + 2] = z;

        // Color: per-ring hue offset, treble shimmer adds sparkle.
        const hueOffset = r * 60;
        const hue = ((fx.hueDeg + hueOffset) / 360) % 1;
        const sparkle = Math.sin(t * 4 + s * 0.21) * 0.5 + 0.5;
        const v = Math.min(
          1.6,
          0.7 +
            (r === 0 ? this.bassEnv : r === 1 ? this.midEnv : this.trebEnv) * 0.5 +
            sparkle * 0.18 +
            this.beatEnv * 0.2,
        );
        const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
        writeHsvToRgb(
          colors,
          o3,
          hue < 0 ? hue + 1 : hue,
          0.85,
          v,
          intensityBoost,
        );
      }
    }
    (this.orbital.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.orbital.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    const orbMat = this.orbital.material as THREE.PointsMaterial;
    orbMat.size = Math.max(10, 44 * p.starSize) * (1 + this.beatEnv * 0.5);
    orbMat.opacity = Math.min(1, 0.85 + this.beatEnv * 0.15) * Math.max(0.4, p.opacity);
    // Suppress unused-arg warning while still keeping the arg for future use.
    void energy;
  }

  // -------------------------------------------------------------------------
  // Galaxy Flythrough — spiral disc + warp-streak foreground
  // -------------------------------------------------------------------------

  private updateGalaxy(dt: number, energy: number, fx: ConceptEffects): void {
    const t = this.elapsed;
    const p = this.params;
    const positions = this.galaxyDiscPos;
    const colors = this.galaxyDiscCol;
    const seeds = this.galaxyDiscSeed;
    const arms = BioclastScene.GALAXY_ARMS;
    const activeDiscCount = fixedLayerCount(
      BioclastScene.GALAXY_DISC_COUNT,
      p.qualityTier,
    );
    const activeStreakCount = fixedLayerCount(
      BioclastScene.GALAXY_STREAK_COUNT,
      p.qualityTier,
    );
    this.galaxyDisc.geometry.setDrawRange(0, activeDiscCount);
    this.galaxyStreaks.geometry.setDrawRange(0, activeStreakCount * 2);
    // Disc radius and arm twist factor define the visual character.
    const galaxyR = RADIUS * 1.8;
    const armTwist = 5.2; // total radians of twist from core to rim
    // Bass-driven core pulse — the galaxy core breathes in/out on bass.
    const corePulse = 1 + this.bassEnv * 0.18;
    // Slow overall rotation so the spiral feels alive.
    this.galaxyRotation += dt * 0.04 * (1 + this.bassEnv * 0.5);
    const galRot = this.galaxyRotation;

    for (let i = 0; i < activeDiscCount; i++) {
      const i4 = i * 4;
      const r0 = seeds[i4];
      const arm = seeds[i4 + 1];
      const angJitter = seeds[i4 + 2];
      const yJit = seeds[i4 + 3];
      // Each particle orbits at radius r0*galaxyR. Its base angle is
      // arm-locked + a logarithmic-spiral twist (theta = base + r * twist).
      const r = r0 * galaxyR * corePulse;
      const baseAng = (arm / arms) * Math.PI * 2;
      const theta = baseAng + r0 * armTwist + galRot + angJitter;
      const x = Math.cos(theta) * r;
      const z = Math.sin(theta) * r;
      const y = yJit * RADIUS * (corePulse * 0.4 + 0.6);
      const i3 = i * 3;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;

      // Color: golden core → cyan rim. Add per-particle twinkle.
      const innerness = 1 - r0; // 0..1, 1 = at core
      const twinkle = Math.sin(t * 1.7 + i * 0.0193) * 0.5 + 0.5;
      // Hue: 50° (gold) at core to 200° (cyan) at rim — interpolate.
      const hueDeg = 200 - innerness * 150;
      const hue = ((fx.hueDeg + hueDeg) / 360) % 1;
      const v = Math.min(
        1.7,
        0.55 + innerness * 0.65 + twinkle * 0.18 + this.beatEnv * 0.12,
      );
      const sat = 0.55 + (1 - innerness) * 0.35;
      const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
      writeHsvToRgb(
        colors,
        i3,
        hue < 0 ? hue + 1 : hue,
        sat,
        v,
        intensityBoost,
      );
    }
    this.markGeometryAttributesUpdated(
      this.galaxyDisc.geometry,
      activeDiscCount,
      ["position", "color"],
    );
    const discMat = this.galaxyDisc.material as THREE.PointsMaterial;
    discMat.size = Math.max(4.5, 24 * p.starSize) * (1 + this.beatEnv * 0.3);
    discMat.opacity = Math.min(1, 0.9 + this.beatEnv * 0.1) * Math.max(0.4, p.opacity);

    // Warp-streak foreground in world space. Each streak is a line segment
    // whose depth advances with time — when it reaches the origin it wraps
    // back to the far end. Keeping this unparented avoids camera lock.
    const sPos = this.galaxyStreaksPos;
    const sCol = this.galaxyStreaksCol;
    const sSeed = this.galaxyStreaksSeed;
    // Flow rate scales with overall energy + bass kick.
    const flow = 0.8 + p.flowSpeed * 0.4 + energy * 0.6 + this.bassEnv * 0.4;
    this.galaxyTravel += dt * flow;
    const travelT = this.galaxyTravel;
    const farZ = -RADIUS * 4.5;
    const nearZ = -RADIUS * 0.1;
    const streakLen = RADIUS * 0.18;
    for (let i = 0; i < activeStreakCount; i++) {
      const i3 = i * 3;
      const az = sSeed[i3];
      const el = sSeed[i3 + 1];
      const phase0 = sSeed[i3 + 2];
      // 0..1 wraps repeatedly; 0 = at far Z, 1 = at near Z.
      const phase = ((phase0 + travelT * (0.06 + (i % 7) * 0.005)) % 1 + 1) % 1;
      const z = farZ + (nearZ - farZ) * phase;
      // Radial offset grows toward camera (perspective-style spread).
      const rad = (RADIUS * 0.05 + Math.abs(z) * 0.6) * 0.6;
      const x = Math.cos(az) * rad;
      const y = Math.sin(az) * rad + el * RADIUS * 0.3;
      // Streak tail extends *toward* the camera (smaller |z|).
      const o = i * 6;
      sPos[o] = x;
      sPos[o + 1] = y;
      sPos[o + 2] = z;
      sPos[o + 3] = x;
      sPos[o + 4] = y;
      sPos[o + 5] = z + streakLen; // toward lens
      // Streak brightness fades at far Z (just emerged) and at near Z (about to disappear).
      const fade = phase < 0.1 ? phase / 0.1 : phase > 0.9 ? (1 - phase) / 0.1 : 1;
      const v = Math.min(1.6, 0.7 + this.beatEnv * 0.4) * fade;
      const hue = ((fx.hueDeg + (1 - phase) * 180) / 360) % 1;
      const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
      writeHsvToRgb(sCol, o, hue < 0 ? hue + 1 : hue, 0.7, v, intensityBoost);
      sCol[o + 3] = sCol[o] * 0.3;
      sCol[o + 4] = sCol[o + 1] * 0.3;
      sCol[o + 5] = sCol[o + 2] * 0.3;
    }
    this.markGeometryAttributesUpdated(
      this.galaxyStreaks.geometry,
      activeStreakCount * 2,
      ["position", "color"],
    );
    const streakMat = this.galaxyStreaks.material as THREE.LineBasicMaterial;
    streakMat.opacity = Math.min(0.95, 0.5 + energy * 0.3 + this.beatEnv * 0.2);
  }

  // -------------------------------------------------------------------------
  // Aurora Forest — vertical glowing ribbons you fly through
  // -------------------------------------------------------------------------

  private updateAuroraForest(energy: number, fx: ConceptEffects): void {
    const t = this.elapsed;
    const p = this.params;
    const positions = this.auroraForestPos;
    const colors = this.auroraForestCol;
    const seeds = this.auroraForestSeed;
    const ribbons = BioclastScene.AURORA_FOREST_RIBBONS;
    const activeCount = fixedLayerCount(
      BioclastScene.AURORA_FOREST_COUNT,
      p.qualityTier,
    );
    this.auroraForest.geometry.setDrawRange(0, activeCount);
    const forestRadius = RADIUS * 1.4;
    const ribbonHeight = RADIUS * 1.8;
    const ribbonWidth = RADIUS * 0.18;

    for (let i = 0; i < activeCount; i++) {
      const i2 = i * 2;
      const ribbon = seeds[i2];
      const within = seeds[i2 + 1]; // 0..1 along ribbon height
      // Place ribbon around the circle, with mild radial wobble that
      // changes per ribbon so the forest doesn't feel mechanical.
      const ribbonAng = (ribbon / ribbons) * Math.PI * 2 +
        Math.sin(t * 0.07 + ribbon) * 0.05;
      // Ribbon center (X, Z), scaled by mid envelope so the whole forest
      // breathes in and out gently with the music.
      const radial = forestRadius * (1 + this.midEnv * 0.06);
      const cx = Math.cos(ribbonAng) * radial;
      const cz = Math.sin(ribbonAng) * radial;
      // Within-ribbon: points distributed along height + horizontal width
      // that sways with mid + treble. Use the bottom half of `within` for
      // ribbon vertical, the top half tracks horizontal wave.
      const vY = within - 0.5; // -0.5..0.5
      // Horizontal wave: each ribbon has a phase, mids drive amplitude.
      const wave = Math.sin(within * 8 + t * (1.4 + ribbon * 0.13)) *
        ribbonWidth *
        (0.6 + this.midEnv * 0.9 + this.trebEnv * 0.4);
      // Tangent vector around the forest circle so the ribbon "faces inward."
      const tx = -Math.sin(ribbonAng);
      const tz = Math.cos(ribbonAng);
      const x = cx + tx * wave;
      const y = vY * ribbonHeight;
      const z = cz + tz * wave;
      const i3 = i * 3;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;

      // Color: per-ribbon hue + treble shimmer + height-driven brightness.
      const ribbonHue = (ribbon / ribbons) * 0.6;
      const hue = ((fx.hueDeg / 360) + ribbonHue + Math.sin(t * 0.2) * 0.05) % 1;
      const heightFade = 1 - Math.abs(vY * 2); // bright in middle, fade at top/bottom
      const v = Math.min(
        1.6,
        0.5 + heightFade * 0.6 + this.trebEnv * 0.5 + this.beatEnv * 0.2,
      );
      const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
      writeHsvToRgb(
        colors,
        i3,
        hue < 0 ? hue + 1 : hue,
        0.85,
        v,
        intensityBoost,
      );
    }
    this.markGeometryAttributesUpdated(
      this.auroraForest.geometry,
      activeCount,
      ["position", "color"],
    );
    const aMat = this.auroraForest.material as THREE.PointsMaterial;
    aMat.size = Math.max(5, 28 * p.starSize) * (1 + this.beatEnv * 0.5);
    aMat.opacity = Math.min(1, 0.85 + this.beatEnv * 0.15) * Math.max(0.4, p.opacity);
    void energy;
  }

  // -------------------------------------------------------------------------
  // Boid Flock — separation/alignment/cohesion + beat-driven fear
  // -------------------------------------------------------------------------

  private updateBoids(dt: number, energy: number, fx: ConceptEffects): void {
    const p = this.params;
    const state = this.boidsState;
    const positions = this.boidsPos;
    const colors = this.boidsCol;
    const N = fixedLayerCount(BioclastScene.BOID_COUNT, p.qualityTier);
    this.boids.geometry.setDrawRange(0, N);

    // ---- Compute global forces once (avoid per-boid O(N²) cost) ----
    // Center of mass + average velocity over the whole flock.
    let cmx = 0, cmy = 0, cmz = 0;
    let avx = 0, avy = 0, avz = 0;
    for (let i = 0; i < N; i++) {
      const i6 = i * 6;
      cmx += state[i6];
      cmy += state[i6 + 1];
      cmz += state[i6 + 2];
      avx += state[i6 + 3];
      avy += state[i6 + 4];
      avz += state[i6 + 5];
    }
    cmx /= N; cmy /= N; cmz /= N;
    avx /= N; avy /= N; avz /= N;

    // Beat-driven "fear": kicks pull every boid sharply toward the center
    // (tightens the flock); treble fans them out.
    const fear = this.bassEnv * 1.6 + this.beatEnv * 1.2;
    const calm = this.trebEnv * 0.8;
    const cohereGain = (0.6 + fear * 1.4) * 0.0008;
    const alignGain = 0.04 + this.midEnv * 0.06;
    // Separation: every Kth boid samples from a random sibling. Cheap.
    const sepRadius = RADIUS * (0.04 + calm * 0.05);
    const sepGain = 1.2;
    // Boundary spring keeps the flock inside a soft sphere.
    const boundR = RADIUS * 1.3;
    const maxSpeed = 220 * (1 + energy * 0.6);
    const steeringScale = Math.min(3, dt * 60);
    const damping = Math.exp(-dt * 0.12);

    for (let i = 0; i < N; i++) {
      const i6 = i * 6;
      const px = state[i6];
      const py = state[i6 + 1];
      const pz = state[i6 + 2];
      let vx = state[i6 + 3];
      let vy = state[i6 + 4];
      let vz = state[i6 + 5];

      // Cohesion: steer toward COM.
      vx += (cmx - px) * cohereGain * steeringScale;
      vy += (cmy - py) * cohereGain * steeringScale;
      vz += (cmz - pz) * cohereGain * steeringScale;
      // Alignment: blend a fraction of the average velocity.
      vx += (avx - vx) * alignGain * 0.05 * steeringScale;
      vy += (avy - vy) * alignGain * 0.05 * steeringScale;
      vz += (avz - vz) * alignGain * 0.05 * steeringScale;
      // Separation from a single sampled sibling (cheap O(N), good enough).
      const sIdx = (i + Math.floor(p ? this.elapsed * 13 : 0) + 1) % N;
      const s6 = sIdx * 6;
      const dx = px - state[s6];
      const dy = py - state[s6 + 1];
      const dz = pz - state[s6 + 2];
      const dsq = dx * dx + dy * dy + dz * dz;
      if (dsq < sepRadius * sepRadius && dsq > 0.001) {
        const inv = sepGain / Math.sqrt(dsq);
        vx += dx * inv * steeringScale;
        vy += dy * inv * steeringScale;
        vz += dz * inv * steeringScale;
      }
      // Soft outer boundary.
      const dist = Math.sqrt(px * px + py * py + pz * pz) || 0.001;
      if (dist > boundR) {
        const k = ((dist - boundR) / boundR) * 0.04;
        vx -= (px / dist) * k * 200 * steeringScale;
        vy -= (py / dist) * k * 200 * steeringScale;
        vz -= (pz / dist) * k * 200 * steeringScale;
      }

      vx *= damping;
      vy *= damping;
      vz *= damping;

      // Speed cap.
      const sp = Math.sqrt(vx * vx + vy * vy + vz * vz);
      if (sp > maxSpeed) {
        const k = maxSpeed / sp;
        vx *= k; vy *= k; vz *= k;
      }

      // Integrate.
      const npx = px + vx * dt;
      const npy = py + vy * dt;
      const npz = pz + vz * dt;
      state[i6] = npx;
      state[i6 + 1] = npy;
      state[i6 + 2] = npz;
      state[i6 + 3] = vx;
      state[i6 + 4] = vy;
      state[i6 + 5] = vz;

      const i3 = i * 3;
      positions[i3] = npx;
      positions[i3 + 1] = npy;
      positions[i3 + 2] = npz;

      // Color: hue from speed, brightness from beat. Twinkle per-boid.
      const speed01 = Math.min(1, sp / maxSpeed);
      const twinkle = Math.sin(this.elapsed * 1.7 + i * 0.0193) * 0.5 + 0.5;
      const hue = ((fx.hueDeg + speed01 * 90) / 360) % 1;
      const v = Math.min(1.6, 0.7 + this.beatEnv * 0.4 + twinkle * 0.18);
      const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
      writeHsvToRgb(
        colors,
        i3,
        hue < 0 ? hue + 1 : hue,
        0.85,
        v,
        intensityBoost,
      );
    }
    this.markGeometryAttributesUpdated(
      this.boids.geometry,
      N,
      ["position", "color"],
    );
    const bMat = this.boids.material as THREE.PointsMaterial;
    bMat.size = Math.max(12, 54 * p.starSize) * (1 + this.beatEnv * 0.6);
    bMat.opacity = Math.min(1, 0.85 + this.beatEnv * 0.15) * Math.max(0.4, p.opacity);
  }

  // -------------------------------------------------------------------------
  // Particle Wind Tunnel — organic forward flow + beat polarity flip
  // -------------------------------------------------------------------------

  private updateWind(dt: number, energy: number, fx: ConceptEffects): void {
    const t = this.elapsed;
    const p = this.params;
    const positions = this.windPos;
    const colors = this.windCol;
    const seeds = this.windState;
    const activeCount = fixedLayerCount(BioclastScene.WIND_COUNT, p.qualityTier);
    this.wind.geometry.setDrawRange(0, activeCount);

    // Decay any active flow flip back to forward.
    if (this.windFlipDecay > 0) {
      this.windFlipDecay = Math.max(0, this.windFlipDecay - dt * 1.4);
      if (this.windFlipDecay <= 0) this.windFlowDir = 1;
    }
    // Trigger a flip on a strong beat — the flow direction reverses for
    // ~700ms then snaps back to forward. Reads as the kick "hitting"
    // the wind and momentarily blowing it backward.
    if (this.beatEdge && this.windFlipDecay <= 0) {
      this.windFlowDir = -1;
      this.windFlipDecay = 0.7;
      this.windFlips += 1;
    }

    // Tunnel geometry (camera-local space, since `wind` is parented to camera).
    const tunnelR = RADIUS * 0.55;
    const farZ = -RADIUS * 4.5;
    const nearZ = -RADIUS * 0.05;
    // Flow rate scales with energy + bass, signed by current flowDir.
    const flow = (0.4 + p.flowSpeed * 0.3 + energy * 0.5 + this.bassEnv * 0.4) *
      this.windFlowDir;
    this.windTravel += dt * flow;
    const travelT = this.windTravel;

    // Curl strength — how much the streamline bends. Mid-range adds curl.
    const curlAmp = 0.18 + this.midEnv * 0.4;

    for (let i = 0; i < activeCount; i++) {
      const i4 = i * 4;
      const r0 = seeds[i4];
      const theta0 = seeds[i4 + 1];
      const phase0 = seeds[i4 + 2];
      const lane = seeds[i4 + 3];
      // 0..1 wraps repeatedly. flowDir baked into travelT; positive flow
      // makes phase grow → particle moves toward lens.
      const phase = ((phase0 + travelT * 0.18 * lane) % 1 + 1) % 1;
      const z = farZ + (nearZ - farZ) * phase;
      // Curl: theta drifts as a sin-of-z so streamlines weave gently.
      const theta = theta0 + Math.sin(z * 0.0035 + t * 0.4) * curlAmp;
      const r = tunnelR * (0.85 + Math.sin(theta * 3 + t * 0.7) * 0.08) * r0;
      const x = Math.cos(theta) * r;
      const y = Math.sin(theta) * r;
      const i3 = i * 3;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;

      // Color: hue shifts along the tunnel length (depth-based gradient)
      // + treble shimmer per-particle.
      const twinkle = Math.sin(t * 1.7 + i * 0.0193) * 0.5 + 0.5;
      const hue = ((fx.hueDeg + (1 - phase) * 120 + twinkle * 8) / 360) % 1;
      // Fade in/out at the ends so particles don't pop.
      const fadeNear = phase > 0.92 ? (1 - phase) / 0.08 : 1;
      const fadeFar = phase < 0.08 ? phase / 0.08 : 1;
      const fade = fadeNear * fadeFar;
      const v = Math.min(1.5, 0.6 + this.beatEnv * 0.4 + twinkle * 0.2) * fade;
      const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
      writeHsvToRgb(
        colors,
        i3,
        hue < 0 ? hue + 1 : hue,
        0.7,
        v,
        intensityBoost,
      );
    }
    this.markGeometryAttributesUpdated(
      this.wind.geometry,
      activeCount,
      ["position", "color"],
    );
    const wMat = this.wind.material as THREE.PointsMaterial;
    wMat.size = Math.max(5, 26 * p.starSize) * (1 + this.beatEnv * 0.4);
    wMat.opacity = Math.min(1, 0.85 + this.beatEnv * 0.15) * Math.max(0.4, p.opacity);
  }

  // -------------------------------------------------------------------------
  // Liquid Mercury Pool — wave surface with ripple sources
  // -------------------------------------------------------------------------

  private updateMercury(dt: number, energy: number, fx: ConceptEffects): void {
    const t = this.elapsed;
    const p = this.params;
    const segments = BioclastScene.MERCURY_SEGMENTS;
    const verts = this.mercuryGeo.attributes.position as THREE.BufferAttribute;
    const arr = verts.array as Float32Array;
    const size = RADIUS * 12;

    // Spawn a new ripple on every strong treble pulse (or a beat).
    if (this.trebleEdge || this.beatEdge) {
      // Random surface point for the ripple drop.
      const rx = (hash(this.elapsed * 7.3) - 0.5) * size * 0.7;
      const rz = (hash(this.elapsed * 11.7) - 0.5) * size * 0.7;
      const power = Math.min(1.8, this.trebEnv * 1.5 + this.features.beat * 0.8);
      this.mercuryRipples.push({ x: rx, z: rz, age: 0, power });
      this.mercuryRippleSpawns += 1;
      if (this.mercuryRipples.length > 8) this.mercuryRipples.shift();
    }
    // Decay ripples.
    for (const r of this.mercuryRipples) {
      r.age += dt;
      r.power *= Math.exp(-1.2 * dt);
    }
    this.mercuryRipples = this.mercuryRipples.filter((r) => r.power > 0.04);

    // Per-vertex wave height. Sum of:
    //   1. Two slow base sines (bass + mid)
    //   2. Per-ripple expanding circular wave from each active source
    const heightScale = RADIUS * 0.18;
    const bass = this.bassEnv;
    const mid = this.midEnv;
    for (let i = 0; i < arr.length / 3; i++) {
      const ix = i * 3;
      const x = arr[ix];
      const z = arr[ix + 2];
      // Base sines: low-frequency rolling wave that lives in bass + mid.
      let y =
        Math.sin(x * 0.0018 + t * 0.5 + bass * 1.5) * heightScale * (0.4 + bass * 0.6) +
        Math.cos(z * 0.0021 + t * 0.7 + mid * 1.2) * heightScale * (0.3 + mid * 0.5);
      // Add ripple contributions.
      for (const r of this.mercuryRipples) {
        const dx = x - r.x;
        const dz = z - r.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        const front = r.age * 900; // expanding radius
        const widthScale = 60 / Math.max(40, d * 0.5 + 80);
        const env = Math.exp(-Math.abs(d - front) * 0.012) * widthScale;
        y += Math.sin((d - front) * 0.025) * heightScale * 1.3 * r.power * env;
      }
      arr[ix + 1] = y;
    }
    verts.needsUpdate = true;

    const mat = this.mercury.material as THREE.MeshBasicMaterial;
    // Glossy color: hue shifts subtly with time, brightness pulses on energy.
    mat.color.copy(paletteColor(fx.hueDeg + 15, 0.7 + energy * 0.2 + this.beatEnv * 0.18));
    mat.opacity = Math.min(0.42, 0.1 + energy * 0.18 + this.beatEnv * 0.1) *
      Math.max(0.3, p.opacity);
    // Suppress unused-arg warning while using the variable above.
    void segments;
  }

  // -------------------------------------------------------------------------
  // Mountain Range — fly forward through persistent terrain
  // -------------------------------------------------------------------------

  private updateMountains(dt: number, energy: number, fx: ConceptEffects): void {
    const W = BioclastScene.MOUNTAINS_W;
    const H = BioclastScene.MOUNTAINS_H;
    const positions = this.mountainsPos;
    const colors = this.mountainsCol;
    const heights = this.mountainsHeight;
    const p = this.params;

    // Camera "flight speed" through the range. Bass + flow speed control
    // it; flowSpeed = 0 freezes the terrain so the user can hold a frame.
    const flightSpeed = p.flowSpeed * (0.5 + energy * 0.3 + this.bassEnv * 0.4);
    this.mountainsScrollOffset += dt * flightSpeed * 8;

    // Bass-driven height multiplier — peaks visibly grow on kicks.
    const heightMul = 1 + this.bassEnv * 0.35 + this.beatEnv * 0.18;

    const cellW = (RADIUS * 6.5) / (W - 1);
    const cellD = (RADIUS * 7.0) / (H - 1);
    const baseY = -RADIUS * 0.45;
    const heightScale = RADIUS * 1.6 * heightMul;
    const intensityBoost = Math.max(0.5, Math.min(3, p.intensity));
    let seg = 0;

    // Sample terrain at (col, row + scrollOffset). The offset wraps
    // continuously through the 0..H-1 range so the terrain repeats —
    // since the noise was baked into the height array tile, you fly
    // through "infinite" mountains.
    const sample = (col: number, rowF: number): number => {
      // Wrap rowF into [0, H).
      const r = ((rowF % H) + H) % H;
      const r0 = Math.floor(r);
      const r1 = (r0 + 1) % H;
      const ft = r - r0;
      const a = heights[r0 * W + col];
      const b = heights[r1 * W + col];
      return a + (b - a) * ft;
    };

    // Horizontal ridge lines (one per row).
    for (let row = 0; row < H; row++) {
      const rowF = row + this.mountainsScrollOffset;
      // Only draw rows that fall within the visible depth strip.
      if (row >= H) break;
      const z = -row * cellD;
      for (let col = 0; col < W - 1; col++) {
        if (seg >= BioclastScene.MOUNTAINS_SEGMENTS) break;
        const x0 = (col - (W - 1) / 2) * cellW;
        const x1 = (col + 1 - (W - 1) / 2) * cellW;
        const h0 = sample(col, rowF);
        const h1 = sample(col + 1, rowF);
        const y0 = baseY + h0 * heightScale;
        const y1 = baseY + h1 * heightScale;
        const o = seg * 6;
        positions[o] = x0;
        positions[o + 1] = y0;
        positions[o + 2] = z;
        positions[o + 3] = x1;
        positions[o + 4] = y1;
        positions[o + 5] = z;
        // Color: hue shifts with elevation (low = cool blue, high = warm
        // amber/white), brightness scales with the peak.
        const hue0 = ((fx.hueDeg + (1 - h0) * 200) / 360) % 1;
        const hue1 = ((fx.hueDeg + (1 - h1) * 200) / 360) % 1;
        const v0 = Math.min(1.5, 0.4 + h0 * 1.1);
        const v1 = Math.min(1.5, 0.4 + h1 * 1.1);
        writeHsvToRgb(colors, o, hue0 < 0 ? hue0 + 1 : hue0, 0.7, v0, intensityBoost);
        writeHsvToRgb(colors, o + 3, hue1 < 0 ? hue1 + 1 : hue1, 0.7, v1, intensityBoost);
        seg++;
      }
    }
    // Vertical struts every 6th column to sketch in depth without
    // choking the GPU on a fully dense wireframe.
    for (let col = 0; col < W; col += 6) {
      for (let row = 0; row < H - 1; row++) {
        if (seg >= BioclastScene.MOUNTAINS_SEGMENTS) break;
        const x = (col - (W - 1) / 2) * cellW;
        const z0 = -row * cellD;
        const z1 = -(row + 1) * cellD;
        const h0 = sample(col, row + this.mountainsScrollOffset);
        const h1 = sample(col, (row + 1) + this.mountainsScrollOffset);
        const y0 = baseY + h0 * heightScale;
        const y1 = baseY + h1 * heightScale;
        const o = seg * 6;
        positions[o] = x;
        positions[o + 1] = y0;
        positions[o + 2] = z0;
        positions[o + 3] = x;
        positions[o + 4] = y1;
        positions[o + 5] = z1;
        const avgH = (h0 + h1) * 0.5;
        const hue = ((fx.hueDeg + (1 - avgH) * 200) / 360) % 1;
        const v = Math.min(1.4, 0.35 + avgH * 0.95);
        writeHsvToRgb(colors, o, hue < 0 ? hue + 1 : hue, 0.65, v, intensityBoost);
        colors[o + 3] = colors[o];
        colors[o + 4] = colors[o + 1];
        colors[o + 5] = colors[o + 2];
        seg++;
      }
    }
    // Zero-fill the rest of the buffer if we under-wrote.
    while (seg < BioclastScene.MOUNTAINS_SEGMENTS) {
      const o = seg * 6;
      positions[o] = 0; positions[o + 1] = 0; positions[o + 2] = 0;
      positions[o + 3] = 0; positions[o + 4] = 0; positions[o + 5] = 0;
      seg++;
    }
    (this.mountains.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.mountains.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    const mountainsMat = this.mountains.material as THREE.LineBasicMaterial;
    mountainsMat.opacity = Math.min(0.95, 0.6 + energy * 0.3 + this.beatEnv * 0.15);
  }

  // -------------------------------------------------------------------------
  // Volumetric Energy Orb — raymarched folded field inside a sphere
  // -------------------------------------------------------------------------

  private updateEnergyOrb(dt: number, energy: number, fx: ConceptEffects): void {
    const p = this.params;
    const bass = this.bassEnv;
    const mid = this.midEnv;
    const treble = this.trebEnv;
    const beat = this.beatEnv;

    const sizePulse = 1 + beat * 0.025 + bass * 0.035;
    this.energyOrb.scale.setScalar(RADIUS * Math.max(0.08, p.energyOrbSize) * sizePulse);
    this.energyOrb.rotation.y += dt * p.energyOrbRotation * (0.55 + mid * 0.85 + energy * 0.25);
    this.energyOrb.rotation.x = Math.sin(this.elapsed * 0.11) * 0.12 * p.energyOrbRotation;
    this.energyOrb.updateMatrixWorld();

    this.energyOrbLocalCam.copy(this.camera.position);
    this.energyOrb.worldToLocal(this.energyOrbLocalCam);

    const primary = this.energyOrbMaterial.uniforms.uPrimaryColor.value as THREE.Color;
    const secondary = this.energyOrbMaterial.uniforms.uSecondaryColor.value as THREE.Color;
    const halo = this.energyOrbAtmosphereMaterial.uniforms.uColor.value as THREE.Color;
    setColorStyle(primary, p.energyOrbPrimaryColor, DEFAULT_BIOCLAST.energyOrbPrimaryColor);
    setColorStyle(secondary, p.energyOrbSecondaryColor, DEFAULT_BIOCLAST.energyOrbSecondaryColor);
    halo.copy(primary).lerp(secondary, 0.18 + treble * 0.18);

    const orbUniforms = this.energyOrbMaterial.uniforms;
    orbUniforms.uTime.value += dt * p.energyOrbSpeed * (0.7 + mid * 0.9 + beat * 0.22);
    orbUniforms.uLocalCamPos.value.copy(this.energyOrbLocalCam);
    orbUniforms.uDensity.value = p.energyOrbDensity * (1 + bass * 0.25 + beat * 0.1);
    orbUniforms.uFractalIters.value = Math.max(2, Math.min(8, p.energyOrbFractalIters + beat * 0.85));
    orbUniforms.uFractalScale.value = p.energyOrbFractalScale + mid * 0.035;
    orbUniforms.uFractalDecay.value = p.energyOrbFractalDecay + treble * 1.8;
    orbUniforms.uInternalAnim.value = 0.75 + p.energyOrbSpeed * (0.75 + mid * 0.7);
    orbUniforms.uSmoothness.value = p.energyOrbSmoothness + beat * 0.006;
    orbUniforms.uAsymmetry.value = p.energyOrbAsymmetry + Math.sin(this.elapsed * 0.18 + fx.hueDeg * 0.01) * 0.04;
    orbUniforms.uBeat.value = beat;
    orbUniforms.uIntensity.value = Math.max(0.2, Math.min(4, p.intensity)) * (0.62 + energy * 0.08);

    const haloUniforms = this.energyOrbAtmosphereMaterial.uniforms;
    haloUniforms.uGlow.value = p.energyOrbAtmosphereGlow * (1 + treble * 0.35 + beat * 0.45);
    haloUniforms.uLevel.value = p.energyOrbAtmosphereLevel;
    haloUniforms.uBeat.value = beat;
    this.energyOrbAtmosphere.scale.setScalar(
      p.energyOrbAtmosphereScale + beat * 0.012 + bass * 0.01,
    );
  }

  // -------------------------------------------------------------------------
  // Waveform overlay — MilkDrop-style audio waveform line
  // -------------------------------------------------------------------------
  //
  // Camera-locked Line drawn on top of every other layer. Each frame we:
  //   1. Read the time-domain PCM samples from the audio analyzer
  //   2. Downsample 1024 → WAVEFORM_SAMPLES with simple decimation
  //   3. Map samples to screen positions in CAMERA-LOCAL space (Z = -1)
  //      so the line sits a fixed distance ahead of the lens regardless
  //      of camera mode/zoom — true HUD overlay.
  //   4. Three styles supported:
  //      - "line"   horizontal sweep, amplitude controls Y
  //      - "dual"   two horizontal lines, second offset down (stereo feel)
  //      - "radial" circle around screen center, amplitude is radial offset
  // -------------------------------------------------------------------------

  private updateWaveform(fx: ConceptEffects): void {
    const wav = this.features.waveform;
    if (!wav || wav.length === 0) {
      // No waveform available (e.g. preanalyzed source) — collapse the line.
      const positions = this.waveformPos;
      for (let i = 0; i < positions.length; i++) positions[i] = 0;
      (this.waveformGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      return;
    }
    const N = BioclastScene.WAVEFORM_SAMPLES;
    const positions = this.waveformPos;
    const p = this.params;
    const style = p.waveformStyle;
    // Camera-local space: Z = -1 puts the line right in front of the lens
    // (just inside the near plane). X spans -aspect..+aspect approximately
    // (we use ±0.55 for ~16:9 framing without spilling on portrait).
    const z = -1;
    const halfWidth = 0.55;
    // Amplitude scale grows with treble shimmer + intensity so the
    // waveform feels alive without dominating the scene.
    const amp =
      0.18 *
      (0.6 + p.trebleShimmer * 0.25 + this.beatEnv * 0.18) *
      Math.max(0.4, p.intensity);

    // Decimation: pick every (wav.length / N) sample to fit our buffer.
    const stride = Math.max(1, Math.floor(wav.length / N));

    if (style === "radial") {
      // Circle centered on lens. Amplitude pushes outward from base radius.
      const baseR = 0.35;
      for (let i = 0; i < N; i++) {
        const sample = wav[i * stride] ?? 0;
        const theta = (i / N) * Math.PI * 2;
        const r = baseR + sample * amp * 1.2;
        const o = i * 3;
        positions[o] = Math.cos(theta) * r;
        positions[o + 1] = Math.sin(theta) * r * 0.78; // squashed for aspect
        positions[o + 2] = z;
      }
      // Close the loop by snapping the last sample to the first.
      positions[(N - 1) * 3] = positions[0];
      positions[(N - 1) * 3 + 1] = positions[1];
      positions[(N - 1) * 3 + 2] = z;
    } else if (style === "dual") {
      // Two interleaved scans — one in upper half, one in lower. Both run
      // through the same buffer (256 samples total), 128 per scan. Reads
      // as a stereo "left + right" channel display.
      const half = N / 2;
      for (let i = 0; i < half; i++) {
        const sample = wav[i * stride * 2] ?? 0;
        const x = -halfWidth + (i / (half - 1)) * (halfWidth * 2);
        // Top scan: y around +0.18 with amplitude bouncing.
        positions[i * 3] = x;
        positions[i * 3 + 1] = 0.18 + sample * amp * 0.9;
        positions[i * 3 + 2] = z;
      }
      for (let i = 0; i < half; i++) {
        const sample = wav[(i + half) * stride * 2] ?? 0;
        const x = halfWidth - (i / (half - 1)) * (halfWidth * 2);
        // Bottom scan, inverted x for continuity.
        positions[(half + i) * 3] = x;
        positions[(half + i) * 3 + 1] = -0.18 + sample * amp * 0.9;
        positions[(half + i) * 3 + 2] = z;
      }
    } else {
      // "line" — single horizontal scan across the screen with amplitude as Y.
      for (let i = 0; i < N; i++) {
        const sample = wav[i * stride] ?? 0;
        const x = -halfWidth + (i / (N - 1)) * (halfWidth * 2);
        const o = i * 3;
        positions[o] = x;
        positions[o + 1] = sample * amp;
        positions[o + 2] = z;
      }
    }
    (this.waveformGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;

    // Color tint: pull from the palette so the waveform matches the scene.
    const mat = this.waveform.material as THREE.LineBasicMaterial;
    mat.color.copy(paletteColor(fx.hueDeg + 30, 0.9));
    mat.opacity = Math.min(0.95, 0.55 + this.beatEnv * 0.35);
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private spawnRing(power: number): void {
    this.coreRingSpawns += 1;
    this.rings.unshift({ age: 0, power });
    if (this.rings.length > 4) this.rings.length = 4;
  }

  /**
   * "Spaceship through the field" camera. Drives the camera along a smooth
   * Lissajous-ish 3D curve threaded through the disc, looking forward along
   * the curve so the field rushes past the lens. Bass adds a touch of
   * forward dolly + roll for a kick-driven cinematic feel.
   */
  private updateFlythroughCamera(
    dt: number,
    energy: number,
    fx: ConceptEffects,
  ): void {
    const p = this.params;
    const baseSpeed = Math.max(0, Math.min(3, p.flythroughSpeed));
    // Pump speed slightly on every beat; dolly amount scales the kick.
    const dollyKick = Math.max(0, Math.min(1, p.beatDolly)) * this.beatEnv;
    const speed =
      baseSpeed *
      (1 + energy * 0.5 + this.beatEnv * 0.4 + dollyKick * 1.4);
    this.flyTime += dt * speed * 0.55;
    const t = this.flyTime;

    // Path is built so it never gets *exactly* to the center (preserves the
    // sense of weaving past structures) and stays inside the field volume.
    const r = RADIUS * 0.85;
    const pos = pathAt(t, r, p.cameraOrbit);
    const ahead = pathAt(t + 0.18, r, p.cameraOrbit);

    // Apply zoom as a forward push toward the look-at point. Beat dolly adds
    // a transient forward lurch on top of the steady push so kicks register
    // as actual motion through the field, not just brightness.
    const zoom = Math.max(0.15, Math.min(3.5, p.cameraZoom));
    const fwdX = ahead.x - pos.x;
    const fwdY = ahead.y - pos.y;
    const fwdZ = ahead.z - pos.z;
    const fwdLen = Math.hypot(fwdX, fwdY, fwdZ) || 1;
    const push = (zoom - 1) * RADIUS * 0.35 + dollyKick * RADIUS * 0.18;
    const eyeX = pos.x + (fwdX / fwdLen) * push;
    const eyeY = pos.y + (fwdY / fwdLen) * push;
    const eyeZ = pos.z + (fwdZ / fwdLen) * push;

    this.flythroughEye.set(eyeX, eyeY, eyeZ);
    this.camera.position.lerp(this.flythroughEye, 0.08);
    this.camera.lookAt(ahead.x, ahead.y, ahead.z);

    // Subtle roll on bass beats sells the spaceship feel.
    this.camera.rotation.z +=
      Math.sin(t * 0.7) * 0.06 + this.beatEnv * 0.08 * (p.cameraTilt - 0.5);

    // FOV breathes with the kick + concept hint, slightly wider than orbit
    // mode so the speed reads as motion.
    this.camera.fov =
      72 - energy * 3 + this.beatEnv * 4 + (fx.cameraDistance - 1.6) * 4;
    this.camera.updateProjectionMatrix();
  }
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

/** Builds a VJ-style pin-light sprite shared by every Bioclast point layer. */
export function makeSpriteTexture(): THREE.Texture {
  const size = 96;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const center = size / 2;
  const grad = ctx.createRadialGradient(
    center,
    center,
    0,
    center,
    center,
    center,
  );
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.055, "rgba(255,250,242,1)");
  grad.addColorStop(0.13, "rgba(255,255,255,0.92)");
  grad.addColorStop(0.28, "rgba(255,255,255,0.5)");
  grad.addColorStop(0.55, "rgba(255,255,255,0.14)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "rgba(255,255,255,0.96)";
  ctx.beginPath();
  ctx.arc(center, center, size * 0.025, 0, Math.PI * 2);
  ctx.fill();
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Sample a target position inside one of the emergence shapes. The
 * `seed` is the per-particle stable hash (0..1) so each particle always
 * lands at the same "spot" inside the shape — that way the cohesion
 * force pulls particles into a coherent form rather than every particle
 * fighting for the center.
 *
 * Some shapes also breathe with time — sphere wobbles slowly, torus
 * rotates, ribbon sweeps, blob morphs from a Lissajous-noise field.
 */
function sampleEmergenceTarget(
  target: { x: number; y: number; z: number },
  seed: number,
  index: number,
  shape: "sphere" | "torus" | "cube" | "ribbon" | "blob",
  t: number,
): void {
  // Per-particle deterministic theta/phi from seed + index.
  const a = (seed * 7.31 + index * 0.001) * Math.PI * 2;
  const b = (seed * 4.17) * Math.PI;
  switch (shape) {
    case "sphere": {
      const r = RADIUS * 0.78 * (0.95 + Math.sin(t * 0.6 + seed * 12) * 0.05);
      const sinB = Math.sin(b);
      target.x = Math.cos(a) * sinB * r;
      target.y = Math.cos(b) * r;
      target.z = Math.sin(a) * sinB * r;
      return;
    }
    case "torus": {
      const R = RADIUS * 0.7;
      const r = RADIUS * 0.22 * (0.9 + Math.sin(t * 0.4 + seed * 7) * 0.1);
      const u = a + t * 0.3;
      const v = b * 2;
      target.x = (R + r * Math.cos(v)) * Math.cos(u);
      target.y = r * Math.sin(v);
      target.z = (R + r * Math.cos(v)) * Math.sin(u);
      return;
    }
    case "cube": {
      const face = Math.floor(seed * 6);
      const u = (hash(index * 1.31 + 3.7) - 0.5) * RADIUS * 1.4;
      const v = (hash(index * 7.71 + 9.3) - 0.5) * RADIUS * 1.4;
      const half = RADIUS * 0.7;
      const breath = 1 + Math.sin(t * 0.3) * 0.05;
      switch (face) {
        case 0: target.x = half * breath; target.y = u; target.z = v; return;
        case 1: target.x = -half * breath; target.y = u; target.z = v; return;
        case 2: target.x = u; target.y = half * breath; target.z = v; return;
        case 3: target.x = u; target.y = -half * breath; target.z = v; return;
        case 4: target.x = u; target.y = v; target.z = half * breath; return;
        default: target.x = u; target.y = v; target.z = -half * breath; return;
      }
    }
    case "ribbon": {
      const u = (seed - 0.5) * Math.PI * 4 + t * 0.3;
      const v = (hash(index * 3.7) - 0.5) * 0.2;
      target.x = Math.cos(u) * RADIUS * 0.9 + v * RADIUS;
      target.y = Math.sin(u * 0.7) * RADIUS * 0.4;
      target.z = Math.sin(u) * RADIUS * 0.9;
      return;
    }
    case "blob":
    default: {
      const u = a + t * 0.2;
      const r = RADIUS * 0.65 *
        (0.7 + Math.sin(seed * 5 + t * 0.4) * 0.3);
      target.x = Math.cos(u) * Math.sin(b * 1.3 + t * 0.1) * r;
      target.y = Math.sin(u * 1.7 + seed * 4) * r * 0.7;
      target.z = Math.sin(u) * Math.cos(b * 1.1) * r;
      return;
    }
  }
}

function setColorStyle(color: THREE.Color, value: string, fallback: string): void {
  const next = value.trim() || fallback;
  try {
    color.setStyle(next);
  } catch {
    color.setStyle(fallback);
  }
}

function paletteColor(hueDeg: number, brightness: number): THREE.Color {
  const h = ((hueDeg % 360) + 360) % 360 / 360;
  const c = new THREE.Color();
  c.setHSL(h, 0.85, Math.min(0.85, 0.45 + brightness * 0.35));
  return c;
}

function writeHsvToRgb(
  target: Float32Array,
  offset: number,
  h: number,
  s: number,
  v: number,
  intensity = 1,
): void {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r: number;
  let g: number;
  let b: number;
  switch (((i % 6) + 6) % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    default: r = v; g = p; b = q; break;
  }
  target[offset] = r * intensity;
  target[offset + 1] = g * intensity;
  target[offset + 2] = b * intensity;
}

/** GLSL smoothstep: 0 below `a`, 1 above `b`, smooth Hermite in between. */
function smoothstep(a: number, b: number, x: number): number {
  if (x <= a) return 0;
  if (x >= b) return 1;
  const t = (x - a) / (b - a);
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Concept modes
// ---------------------------------------------------------------------------

/**
 * Per-frame overrides derived from a `ConceptMode`. Treat all fields as
 * read-only; the scene applies them without mutating the user's params.
 */
interface ConceptEffects {
  hueDeg: number;
  flowMul: number;
  bassMul: number;
  midMul: number;
  trebMul: number;
  cameraDistance: number; // multiplier of RADIUS
  cameraHeight: number; // multiplier of RADIUS
  /**
   * Some concepts only read correctly from one camera mode (e.g. warp
   * tunnels need a forward-facing fly-through). When set, the scene forces
   * this mode each frame, overriding the user's manual selection. Leave
   * undefined when the concept works equally well from any angle.
   */
  forceCameraMode?: CameraMode;
}

/**
 * Smooth 3D Lissajous-like path, scaled to the field radius. Tuned so the
 * curve weaves through the disc instead of orbiting it cleanly — that's
 * what gives the flythrough mode its "diving past structures" feel.
 */
function pathAt(
  t: number,
  r: number,
  yawOffset: number,
): { x: number; y: number; z: number } {
  const a = t * 0.7 + yawOffset;
  const b = t * 0.41;
  const c = t * 0.29;
  const x = r * (Math.sin(a) * 0.85 + Math.sin(b * 1.3 + 0.7) * 0.25);
  const y = r * 0.45 * (Math.sin(c * 1.1) * 0.7 + Math.sin(b * 0.6) * 0.4);
  const z = r * (Math.cos(a * 1.07) * 0.85 + Math.cos(b * 1.1 + 1.3) * 0.22);
  return { x, y, z };
}

function applyConceptMode(
  mode: ConceptMode,
  p: BioclastParams,
): ConceptEffects {
  const base: ConceptEffects = {
    hueDeg: p.paletteHue,
    flowMul: 1,
    bassMul: 1,
    midMul: 1,
    trebMul: 1,
    cameraDistance: 1.6,
    cameraHeight: 1.0,
  };
  switch (mode) {
    case "none":
      return base;
    case "aurora":
      return {
        ...base,
        hueDeg: 195,
        flowMul: 0.6,
        midMul: 1.2,
        trebMul: 1.5,
        cameraDistance: 1.7,
        cameraHeight: 1.1,
      };
    case "cathedral":
      return {
        ...base,
        hueDeg: 175,
        flowMul: 0.4,
        bassMul: 0.6,
        midMul: 0.8,
        trebMul: 0.6,
        cameraDistance: 2.1,
        cameraHeight: 0.55,
      };
    case "tunnel":
      return {
        ...base,
        hueDeg: 235,
        flowMul: 2.4,
        midMul: 0.5,
        trebMul: 1.2,
        cameraDistance: 0.9,
        cameraHeight: 0.4,
        forceCameraMode: "flythrough",
      };
    case "bloom":
      return {
        ...base,
        hueDeg: 28,
        flowMul: 0.9,
        bassMul: 2.4,
        midMul: 1.4,
        trebMul: 1.1,
        cameraDistance: 1.5,
        cameraHeight: 1.0,
      };
    case "lava":
      return {
        ...base,
        hueDeg: 12,
        flowMul: 0.45,
        bassMul: 1.6,
        midMul: 1.8,
        trebMul: 0.4,
        cameraDistance: 1.4,
        cameraHeight: 0.7,
      };
    case "nebula":
      return {
        ...base,
        hueDeg: 270,
        flowMul: 0.25,
        bassMul: 0.6,
        midMul: 0.4,
        trebMul: 1.0,
        cameraDistance: 2.4,
        cameraHeight: 1.4,
      };
    case "warp":
      return {
        ...base,
        hueDeg: 215,
        flowMul: 3.2,
        bassMul: 0.9,
        midMul: 0.4,
        trebMul: 1.6,
        cameraDistance: 1.0,
        cameraHeight: 0.3,
        forceCameraMode: "flythrough",
      };
    default:
      return base;
  }
}
